import * as Sentry from "@sentry/react";
import { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback, useContext } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useParams } from "wouter";
import { ArrowLeft, Plus, Minus, ChevronUp, ChevronDown, ChevronsUpDown, ChevronRight, Clock, Leaf, Search, Printer, Bell, MessageSquare, Mail, Calendar, FileText, MapPin, CheckCircle2, Truck, ShoppingCart, X, Users, Check, LogOut, Loader2, TrendingUp } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarWidget } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { motion, AnimatePresence } from "framer-motion";

const KIOSK_SESSION_KEY = 'freshorder_kiosk_session';

// Check if this is a kiosk order - checks both localStorage and URL parameter
// URL parameter is needed for iOS PWA where localStorage isn't shared between Safari and standalone mode
const isKioskMode = (): boolean => {
  // Check URL parameter first (works on iOS PWA)
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('kiosk') === '1') {
    return true;
  }
  
  // Fallback to localStorage check - but validate org slug matches current URL
  const storedSession = localStorage.getItem(KIOSK_SESSION_KEY);
  if (storedSession) {
    try {
      const session = JSON.parse(storedSession);
      // Extract org slug from current URL path (e.g., /org/demo/order/...)
      const urlMatch = window.location.pathname.match(/^\/org\/([^/]+)/);
      const currentOrgSlug = urlMatch ? urlMatch[1] : null;
      
      // Only return true if session belongs to current org and hasn't expired
      if (session.orgSlug === currentOrgSlug) {
        // Also check expiration
        if (session.expiresAt && new Date(session.expiresAt) < new Date()) {
          localStorage.removeItem(KIOSK_SESSION_KEY);
          return false;
        }
        return true;
      }
    } catch {
      // Invalid JSON, clear it
      localStorage.removeItem(KIOSK_SESSION_KEY);
    }
  }
  
  return false;
};

import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { ToastAction } from "@/components/ui/toast";
import { apiRequest, getApiPath, getQueryFn, readPath } from "@/lib/queryClient";
import { forgetCustomerDataOnLeave } from "@/lib/orgQueryCache";
import { ORDERS_CURRENT_OPTIONS, ordersReadFailed, ordersReadyToSeed } from "@/lib/cartSeed";
import { CartTrail } from "@/lib/cartBreadcrumbs";
import { requestedOrderId, signInHref } from "@/lib/orderSummaryLinks";
import { orderTotals } from "@/lib/orderTotals";
import { bellToggleRequest, type BellSwitch } from "@/lib/bellNotifications";
import { track } from "@/lib/analytics";
import { createMenuBackEntry, type MenuBackEntry } from "@/lib/menuBackEntry";
import { setNativeBackSwipe } from "@/lib/native/backSwipe";
import { customerOrderSchema, type CustomerOrderInput, type Product, type Account, type OrderWithDetails, type Category } from "@shared/schema";
import { aliasMatchesSearch } from "@shared/productAliases";
import { ProductHoverCard } from "@/components/product-hover-card";
import { UnitPill } from "@/components/unit-pill";
import { SizeGroupCard } from "@/components/size-group-card";
import { groupProductSizes } from "@shared/productSizes";
import { menuCardLabels } from "@shared/productNames";
import { OrganizationContext } from "@/contexts/OrganizationContext";
import { NativeShellIntegration } from "@/components/native-shell-integration";
import { isNativeApp } from "@/lib/native/platform";
import { getSeenFirstOrderTips, setSeenFirstOrderTips } from "@/lib/native/buyerFirstRun";
import { FirstOrderTips } from "@/components/first-order-tips";
import { OrderingIntro } from "@/components/ordering-intro";
import { currentFirstOrderTip, FIRST_ORDER_ONLY_TIPS, type FirstOrderTip } from "@shared/buyerFirstRun";
import { unbindPushTokenFromVendor } from "@/lib/native/push";
import { hasNativePrinter, printPdfNatively } from "@/lib/native/printer";
import { VendorTopBar } from "@/components/vendor-top-bar";
import { isOrderingAllowed, getOrderingClosedMessage, getAvailableFulfillmentDates, getCountdownForDate, findNextDeliveryCutoff } from "@/lib/frontend-periods";
import { ProduceCrateIcon } from "@/components/produce-crate-icon";
import { CategoryIcon } from "@/components/category-icon";
import freshOrderLogo from "@assets/Logo_-new_clear_1764787161621.png";
import farmBg from "../assets/farm-bg.png";
// Professional potato images - specific varieties
import potatoRusset from "../assets/potato-new.png"; // Russet/White potatoes
import potatoYellow from "../assets/potato-yellow.png"; // Yellow/Yukon varieties
import potatoSmall from "../assets/potato-small.png"; // Small/fingerling potatoes  
import potatoSweet from "../assets/potato-sweet.png"; // Sweet potatoes
import potatoVariety from "../assets/potato-variety.png"; // Red and mixed varieties

// Optimized 300x225 potato images (24-35KB each)
import potato90ctBaking from "@assets/90ct Baking Potato.jpg";
import potato50lbFingerling from "@assets/50lb Fingerling.jpg";
import potato25lbFingerling from "@assets/25lb Fingerling.jpg";
import purpleYam from "@assets/Purple Yam.jpg";
import potato10lbRed from "@assets/10lb Red Potato.jpg";
import potato10lbWhite from "@assets/10lb White Potato.jpg";
import potato10lbYukon from "@assets/10lb Yukon Potato.jpg";
import potato10lbRusset from "@assets/10lb Russet Potato.jpg";
import potato50lbRed from "@assets/50lb Red Potato.jpg";
import potato50lbYukon from "@assets/50lb Yukon Potato.jpg";
import potato50lbWhite from "@assets/50lb White Potato.jpg";
import potatoARed from "@assets/A Red Potato.jpg";
import potatoBRed from "@assets/B Red Potato.jpg";
import potatoCRed from "@assets/C Red Potato.jpg";
// Additional optimized potato images
import potato70ctBaking from "@assets/70ct Baking Potato.jpg";
import potatoCYellow from "@assets/C Yellow Potato.jpg";
import potatoBYellow from "@assets/B Yellow Potato.jpg";
import potatoAYellow from "@assets/A Yellow Potato.jpg";
import potatoSweetPotato from "@assets/Sweet Potato.jpg";
import potatoTrio from "@assets/Trio.jpg";
import potatoParisian from "@assets/Parisian Potato.jpg";
import potatoCWhite from "@assets/C White Potato.jpg";
import potatoBWhite from "@assets/B White Potato.jpg";
import potatoAWhite from "@assets/A White Potato.jpg";
import potato3L from "@assets/3L Potato.jpg";

// Default product image fallback
import defaultProductLogo from "@assets/Logo_-new_clear_1764787161621.png";

// Every read on this page is cached under a key that names the org (orgSlug, last, so
// a prefix invalidation like ["/api/categories"] still reaches it), and names its path
// with readPath. Pinned by tests/client/org-query-cache.test.ts.



// Product image mapping function (potatoes get specific images, others get org logo or FreshOrder logo)
const getProductImage = (productName: string, product?: any, orgLogoUrl?: string | null): string => {
  // First check if the product has a custom uploaded image
  if (product?.imagePath) {
    console.log('Using custom uploaded image');
    return product.imagePath;
  }
  
  // Fallback: use org favicon if provided (square dimensions), otherwise use FreshOrder logo
  const fallbackLogo = orgLogoUrl || defaultProductLogo;
  
  // Check if this is a potato product
  const lowerName = productName.toLowerCase();
  const isPotatoProduct = lowerName.includes('potato') || lowerName.includes('yam') || lowerName.includes('fingerling') || productName === 'Trio' || productName === '3L Potato';
  
  if (!isPotatoProduct) {
    console.log('Using fallback logo for non-potato product');
    return fallbackLogo;
  }
  
  // Exact product name matches for uploaded potato images only
  switch (productName) {
    case "90ct Baking Potato":
      console.log('Using uploaded 90ct baking potato image');
      return potato90ctBaking;
    case "70ct Baking Potato":
      console.log('Using uploaded 70ct baking potato image');
      return potato70ctBaking;
    case "50lb Fingerling":
      console.log('Using uploaded 50lb fingerling image');
      return potato50lbFingerling;
    case "25lb Fingerling":
      console.log('Using uploaded 25lb fingerling image');
      return potato25lbFingerling;
    case "Purple Yam":
      console.log('Using uploaded purple yam image');
      return purpleYam;

    case "10lb Red Potato":
      console.log('Using uploaded 10lb red potato image');
      return potato10lbRed;
    case "10lb White Potato":
      console.log('Using uploaded 10lb white potato image');
      return potato10lbWhite;
    case "10lb Yukon Potato":
      console.log('Using uploaded 10lb yukon potato image');
      return potato10lbYukon;
    case "10lb Russet Potato":
      console.log('Using uploaded 10lb russet potato image');
      return potato10lbRusset;
    case "50lb Red Potato":
      console.log('Using uploaded 50lb red potato image');
      return potato50lbRed;
    case "50lb Yukon Potato":
      console.log('Using uploaded 50lb yukon potato image');
      return potato50lbYukon;
    case "50lb White Potato":
      console.log('Using uploaded 50lb white potato image');
      return potato50lbWhite;
    case "A Red Potato":
      console.log('Using uploaded A red potato image');
      return potatoARed;
    case "B Red Potato":
      console.log('Using uploaded B red potato image');
      return potatoBRed;
    case "C Red Potato":
      console.log('Using uploaded C red potato image');
      return potatoCRed;
    case "C Yellow Potato":
      console.log('Using uploaded C yellow potato image');
      return potatoCYellow;
    case "B Yellow Potato":
      console.log('Using uploaded B yellow potato image');
      return potatoBYellow;
    case "A Yellow Potato":
      console.log('Using uploaded A yellow potato image');
      return potatoAYellow;
    case "Sweet Potato":
      console.log('Using uploaded sweet potato image');
      return potatoSweetPotato;
    case "Parisian Potato":
      console.log('Using uploaded parisian potato image');
      return potatoParisian;
    case "C White Potato":
      console.log('Using uploaded C white potato image');
      return potatoCWhite;
    case "B White Potato":
      console.log('Using uploaded B white potato image');
      return potatoBWhite;
    case "A White Potato":
      console.log('Using uploaded A white potato image');
      return potatoAWhite;
    case "3L Potato":
      console.log('Using uploaded 3L potato image');
      return potato3L;
    case "Trio":
      console.log('Using uploaded trio potato image');
      console.log('Trio image path:', potatoTrio);
      return potatoTrio;

  }

  const lowerProductName = productName.toLowerCase();
  
  // Sweet potatoes
  if (lowerProductName.includes('sweet')) {
    console.log('Using sweet potato image');
    return potatoSweet;
  }
  
  // Red potatoes
  if (lowerProductName.includes('red')) {
    console.log('Using red potato variety image');
    return potatoVariety;
  }
  
  // Yellow/Yukon potatoes
  if (lowerProductName.includes('yellow') || lowerProductName.includes('yukon')) {
    console.log('Using yellow potato image');
    return potatoYellow;
  }
  
  // Small potatoes (3L, 2L, 1L, fingerling)
  if (lowerProductName.includes('3l') || lowerProductName.includes('2l') || lowerProductName.includes('1l') || lowerProductName.includes('fingerling')) {
    console.log('Using small potato image');
    return potatoSmall;
  }
  
  // Default to russet/white for all other potatoes
  console.log('Using default russet potato image');
  return potatoRusset;
};

// View management types
type ViewState = 'categories' | 'items' | 'search' | 'cart' | 'market-report';

// One row in the main-menu order switcher: a submitted order (serverId set) OR an in-progress,
// unsubmitted draft that carries items (isDraft). Keyed by fulfillment date (one order per date).
type SwitcherOrder = {
  date: string;
  type: 'delivery' | 'pickup';
  totalItems: number;
  isDraft: boolean;
  serverId?: number;
};

export default function CustomerEntry() {
  const [account, setAccount] = useState<Account | null>(null);
  const [currentView, setCurrentView] = useState<ViewState>('categories');
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [cartDrawerMode, setCartDrawerMode] = useState<'closed' | 'quick-view' | 'full-review'>('closed');
  // Recreate-move model: when editing an order and the customer picks a different day/method,
  // we ask intent (pendingMove holds the target). On "Move" we carry the cart to the new date and
  // remember the original order to cancel after the new one is created (moveCancelOrderId).
  const [pendingMove, setPendingMove] = useState<{ date: string; type: 'delivery' | 'pickup' } | null>(null);
  const [moveCancelOrderId, setMoveCancelOrderId] = useState<number | null>(null);
  // Storage key of the moved-from order's date, so a successful move can clear that date's
  // local draft (otherwise it resurfaces in the switcher as a "Not sent yet" ghost).
  const [moveCancelOrderFromKey, setMoveCancelOrderFromKey] = useState<string | null>(null);
  const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [userTyped, setUserTyped] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchInputDesktopRef = useRef<HTMLInputElement>(null);
  const isDeleting = useRef(false);
  const lastSubmittedOrderId = useRef<number | null>(null);
  const isSubmittingOrder = useRef(false);
  const submissionId = useRef(crypto.randomUUID());
  const [editingQuantity, setEditingQuantity] = useState<number | null>(null);
  const [editQuantityValue, setEditQuantityValue] = useState('');
  const [pickupDate, setPickupDate] = useState<string>("");
  const [fulfillmentType, setFulfillmentType] = useState<"delivery" | "pickup">("delivery");
  const [fulfillmentDate, setFulfillmentDate] = useState<string>("");
  const [showNotificationPrefs, setShowNotificationPrefs] = useState(false);
  const [notificationSettings, setNotificationSettings] = useState({
    reminderEnabled: false,
    emailNotificationEnabled: false,
    smsOrderReceipts: false,
    emailOrderReceipts: false
  });
  const [sessionVerified, setSessionVerified] = useState(false);
  const [sessionChecking, setSessionChecking] = useState(true);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [demoTransitioning, setDemoTransitioning] = useState(false);
  const [showDemoWelcome, setShowDemoWelcome] = useState(false);
  
  // Kiosk order flow state
  const [showDeliveryLocationDialog, setShowDeliveryLocationDialog] = useState(false);
  const [showKioskConfirmation, setShowKioskConfirmation] = useState(false);
  const [deliveryLocation, setDeliveryLocation] = useState("");
  const [confirmedDeliveryLocation, setConfirmedDeliveryLocation] = useState<string | null>(null);
  const [lastOrderId, setLastOrderId] = useState<number | null>(null);
  
  // Email verification dialog state
  const [showEmailVerificationDialog, setShowEmailVerificationDialog] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState("");
  const [pendingEmailFeature, setPendingEmailFeature] = useState<'emailNotificationEnabled' | 'emailOrderReceipts' | null>(null);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [accountSwitcherOpen, setAccountSwitcherOpen] = useState(false);
  // Order switcher: a thumb-reachable bottom sheet opened from the bottom cart bar (no top bar) so
  // the multi-order loop (pick order -> adjust qty -> submit -> next) isn't a full-height round-trip.
  const [switcherSheetOpen, setSwitcherSheetOpen] = useState(false);

  // The sticky header deliberately floats ABOVE the cart drawer (z-50 vs z-40) so the
  // omni-present order switcher stays reachable while reviewing a cart. The consequence
  // is that anything the drawer paints in that band is hidden: at 85dvh the drawer's own
  // header slid under the search bar and "x Order #1234" was sheared in half
  // (TestFlight feedback, 2026-09-04). Measure the header so the drawer can stop just
  // below it instead of running underneath. Measured, not hardcoded: the header's height
  // varies with the safe-area inset and whether the vendor bar renders.
  //
  // This MUST be a callback ref, not useRef + useEffect([]). This component has seven
  // early returns above the main render (sessionChecking, productsLoading, ...), so on
  // the first commit the header does not exist yet: a mount effect would read a null
  // ref, bail, and — with an empty dep array — never run again, leaving the height 0
  // and the cap inert. That is exactly how the first attempt at this fix shipped and
  // did nothing on device. A callback ref fires whenever React attaches the node.
  const [stickyHeaderHeight, setStickyHeaderHeight] = useState(0);
  const headerObserver = useRef<ResizeObserver | null>(null);
  const stickyHeaderRef = useCallback((el: HTMLDivElement | null) => {
    headerObserver.current?.disconnect();
    headerObserver.current = null;
    if (!el) return;
    const measure = () => setStickyHeaderHeight(el.getBoundingClientRect().height);
    measure();
    if (typeof ResizeObserver !== "undefined") {
      const ro = new ResizeObserver(measure);
      ro.observe(el);
      headerObserver.current = ro;
    }
  }, []);
  useEffect(() => () => headerObserver.current?.disconnect(), []);
  const [switcherOrders, setSwitcherOrders] = useState<SwitcherOrder[]>([]);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  // Get organization context for fallback logo
  const orgContext = useContext(OrganizationContext);
  const organization = orgContext?.organization;
  const [location, setLocation] = useLocation();
  const params = useParams<{ orgSlug?: string; customUrl?: string }>();
  
  // Refs for cart persistence
  const [isInitialized, setIsInitialized] = useState(false);
  // The cart's history as Sentry breadcrumbs, shipped with one info event per submit
  // (lib/cartBreadcrumbs.ts — ids and quantities only, never names).
  const [cartTrail] = useState(() => new CartTrail());
  useEffect(() => { cartTrail.mount(); }, [cartTrail]);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSavedSnapshotRef = useRef<{ quantities: Record<string, number>, notes: string } | null>(null);
  
  // Extract custom URL from route params (org-scoped) or from legacy path
  // For org-scoped routes: /org/:orgSlug/order/:customUrl -> params.customUrl
  // For legacy routes: /:customUrl -> extract from location
  const customUrl = params.customUrl || (location.startsWith('/') && !location.startsWith('/org/') ? location.slice(1) : null);
  const isCustomUrlRoute = Boolean(customUrl && customUrl !== 'customer');
  const orgSlug = params.orgSlug;

  // Session verification - check if customer is authenticated via httpOnly cookie.
  // A valid session is NOT enough: it must be for the account this page displays
  // (see the account-binding effect below). Until that is confirmed, `account`
  // stays unset, so nothing account-scoped loads and no order form renders.
  const [sessionAccountId, setSessionAccountId] = useState<number | null>(null);
  useEffect(() => {
    const verifySession = async () => {
      try {
        const response = await apiRequest("POST", "/api/customer/verify-session", {});
        const data = await response.json();
        if (data.valid) {
          setSessionAccountId(data.accountId ?? null);
          if (!isCustomUrlRoute) {
            setSessionVerified(true);
            setSessionChecking(false);
          }
          return;
        } else {
          // No toast: the sign-in card we land on explains itself, and on iOS
          // the fixed toast viewport sits under the status bar once the phone
          // field pulls up the keyboard (TestFlight feedback, build 5, 2026-09-11).
          setIsRedirecting(true);
          const loginPath = orgSlug ? signInHref(orgSlug, { order: requestedOrderId(window.location.search) }) : "/";
          setLocation(loginPath);
        }
      } catch (error) {
        // 401 = no session or expired; any other error = redirect to login (no toast, see above)
        setIsRedirecting(true);
        const loginPath = orgSlug ? signInHref(orgSlug, { order: requestedOrderId(window.location.search) }) : "/";
        setLocation(loginPath);
      }

      setSessionChecking(false);
    };

    verifySession();
  }, [orgSlug, setLocation]);

  // localStorage helper functions
  // One canonical definition of "which cart is this": fulfillment TYPE + the date (fulfillment
  // date, else pickup date, else the '_new' sentinel). Type is part of the key because a mixed
  // delivery+pickup org can offer the SAME calendar date for both — their drafts must not collide.
  const cartDateKey = (fd: string, pd: string, type: 'delivery' | 'pickup') => `${type}_${fd || pd || '_new'}`;

  const getStorageKey = (accountId: number | string, dateKey: string) => {
    // Namespace by orgSlug so multi-tenant accountId collisions can never leak across orgs.
    // Fall back to 'default' only if orgSlug is undefined (legacy / non-orgSlug routes).
    // Per-DATE suffix (Phase 2): each (account, fulfillment date) gets its own draft cart,
    // so editing one dated order never clobbers another date's draft.
    const prefix = orgSlug || 'default';
    return `freshorder_cart_${prefix}_${accountId}_${dateKey}`;
  };

  // Prefix for enumerating every per-date cart key belonging to an account.
  const getStorageKeyPrefix = (accountId: number | string) => {
    const prefix = orgSlug || 'default';
    return `freshorder_cart_${prefix}_${accountId}_`;
  };

  // Pre-Phase-2 un-suffixed key (single merged cart). Read once for migration, then deleted.
  const getLegacyStorageKey = (accountId: number | string) => {
    const prefix = orgSlug || 'default';
    return `freshorder_cart_${prefix}_${accountId}`;
  };

  const clearLegacyStorageKeys = (accountId: number | string) => {
    try {
      const legacyKey = `cn_produce_cart_${accountId}`;
      if (localStorage.getItem(legacyKey) !== null) {
        localStorage.removeItem(legacyKey);
      }
    } catch (error) {
      console.error('Failed to clear legacy cart keys:', error);
    }
  };
  
  // Normalize saved cart data - only keep meaningful quantities
  const normalizeSavedCart = (cartData: any): { quantities: Record<string, number>, notes: string } | null => {
    if (!cartData) return null;

    const normalizedQuantities: Record<string, number> = {};

    // Only keep quantities that are > 0
    if (cartData.quantities && typeof cartData.quantities === 'object') {
      Object.entries(cartData.quantities).forEach(([key, value]) => {
        const numValue = Number(value);
        if (numValue > 0) {
          normalizedQuantities[key] = numValue;
        }
      });
    }

    const normalizedNotes = (cartData.notes || '').trim();

    // Only return if there's actual data
    if (Object.keys(normalizedQuantities).length > 0 || normalizedNotes) {
      return {
        quantities: normalizedQuantities,
        notes: normalizedNotes
      };
    }
    
    return null;
  };
  
  const getCurrentPeriodFingerprint = (): string => {
    // The fingerprint is the ISO string of the next delivery cutoff datetime.
    // When a cutoff passes, the fingerprint changes -> any stored cart from the
    // previous period is recognized as stale and discarded.
    if (!windowConfig) return 'no-config';
    try {
      const result = findNextDeliveryCutoff(windowConfig);
      return result ? result.cutoffDate.toISOString() : 'no-cutoff';
    } catch (error) {
      console.error('Failed to compute period fingerprint:', error);
      return 'error';
    }
  };

  const saveCartToStorage = (accountId: number | string, dateKey: string, cartData: any) => {
    try {
      const storageData = {
        ...cartData,
        timestamp: Date.now(),
        accountId,
        periodFingerprint: getCurrentPeriodFingerprint(),
      };

      localStorage.setItem(getStorageKey(accountId, dateKey), JSON.stringify(storageData));
    } catch (error) {
      console.error('Failed to save cart to localStorage:', error);
    }
  };

  const loadCartFromStorage = (accountId: number | string, dateKey: string) => {
    try {
      const stored = localStorage.getItem(getStorageKey(accountId, dateKey));
      if (!stored) return null;

      const data = JSON.parse(stored);

      // TTL: 24 hours (was 7 days). No valid draft cart should outlive a day;
      // anything older is almost certainly stale from a closed period.
      const ONE_DAY = 24 * 60 * 60 * 1000;
      if (Date.now() - data.timestamp > ONE_DAY) {
        clearCartFromStorage(accountId, dateKey);
        return null;
      }

      // Period-fingerprint check: if the stored fingerprint doesn't match the
      // current period's fingerprint, the cutoff has passed and this cart is
      // from a previous period. Discard it.
      const currentFingerprint = getCurrentPeriodFingerprint();
      if (
        data.periodFingerprint &&
        currentFingerprint !== 'no-config' &&
        currentFingerprint !== 'error' &&
        data.periodFingerprint !== currentFingerprint
      ) {
        clearCartFromStorage(accountId, dateKey);
        return null;
      }

      return data;
    } catch (error) {
      console.error('Failed to load cart from localStorage:', error);
      return null;
    }
  };

  const clearCartFromStorage = (accountId: number | string, dateKey: string) => {
    try {
      localStorage.removeItem(getStorageKey(accountId, dateKey));
    } catch (error) {
      console.error('Failed to clear cart from localStorage:', error);
    }
  };

  // Housekeeping: remove only ALREADY-EXPIRED per-date cart keys for this account
  // (timestamp older than the 24h TTL). Safe — never touches a live draft, so it can run
  // freely on init without losing a customer's in-progress cart for any date.
  const sweepExpiredCartKeys = (accountId: number | string) => {
    try {
      const prefix = getStorageKeyPrefix(accountId);
      const ONE_DAY = 24 * 60 * 60 * 1000;
      const toRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(prefix)) continue;
        try {
          const data = JSON.parse(localStorage.getItem(key) || '{}');
          if (!data.timestamp || Date.now() - data.timestamp > ONE_DAY) {
            toRemove.push(key);
          }
        } catch {
          toRemove.push(key); // unparseable → stale
        }
      }
      toRemove.forEach(k => localStorage.removeItem(k));
    } catch (error) {
      console.error('Failed to sweep expired cart keys:', error);
    }
  };

  const form = useForm<CustomerOrderInput>({
    resolver: zodResolver(customerOrderSchema),
    defaultValues: {
      phone: "",
      quantities: {},
      notes: ""
    },
    mode: "onChange"
  });

  // Fetch products. The app-wide default is staleTime: Infinity, which meant a
  // long-lived customer PWA session never saw admin menu changes (products stayed
  // visible after being hidden). The menu is the one surface where staleness is
  // customer-facing, so these two queries refetch on focus after 5 minutes.
  const MENU_STALE_TIME = 5 * 60 * 1000;
  // Every product this page has shown, by id. When the server refuses a line because
  // the product is no longer orderable, the refreshed menu has already lost its name.
  const seenProductNames = useRef(new Map<number, string>());
  // The menu is this account's: with customer types the server filters it and prices
  // it for the signed-in account (group children share one cookie, so the account id
  // is part of the key). Without an account the server returns the default menu.
  const menuAccountId = account?.id ?? null;
  const { data: products = [], isLoading: productsLoading } = useQuery<Product[]>({
    queryKey: ["/api/products/active", menuAccountId, orgSlug],
    queryFn: readPath<Product[]>(menuAccountId ? `/api/products/active?accountId=${menuAccountId}` : "/api/products/active"),
    // While the signed-in account's menu loads, keep showing the default menu rather
    // than an empty page — but never another account's menu (a sibling switch), and
    // never another org's.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[1] == null && previousQuery?.queryKey[2] === orgSlug ? previous : undefined,
    staleTime: MENU_STALE_TIME,
    refetchOnWindowFocus: true,
  });
  useEffect(() => {
    products.forEach((p) => seenProductNames.current.set(p.id, p.name));
  }, [products]);

  // Fetch categories (ordered by sortOrder for consistent ordering across pages)
  const { data: categories = [], isLoading: categoriesLoading } = useQuery<Category[]>({
    queryKey: ["/api/categories", orgSlug],
    queryFn: readPath<Category[]>("/api/categories"),
    staleTime: MENU_STALE_TIME,
    refetchOnWindowFocus: true,
  });

  // Fetch optional market report (public; returns { enabled: false } when off)
  const { data: marketReport } = useQuery<{ enabled: boolean; title?: string; body?: string; contentUpdatedAt?: string }>({
    queryKey: ["/api/market-report", orgSlug],
    queryFn: readPath("/api/market-report"),
  });

  // Fetch order window configuration — use per-account resolved config when an account is known
  const { data: windowConfig, isLoading: windowConfigLoading } = useQuery<{ enabled: boolean; cutoffHour: number; cutoffMinute: number; frequency: string; customDays: number; timezone: string; deliveryEnabled: boolean; pickupEnabled: boolean; pickupFollowsDeliverySchedule: boolean; pickupSchedulingEnabled: boolean; pickupAvailableDays: number; pickupBlackoutDates: string[]; pickupMaxLeadDays: number; deliveryLeadDays: number; pickupCutoffHour: number; pickupCutoffMinute: number; pickupPrepDays: number; deliveryMaxLeadDays: number; deliveryBlackoutDates: string[] }>({
    queryKey: ["/api/order-window-config/resolved", account?.id, orgSlug],
    queryFn: readPath(`/api/order-window-config/resolved/${account?.id}`),
    enabled: Boolean(account?.id),
  });
  
  // Only show deadline messages when order windows are enabled
  const showDeadlineMessages = windowConfig?.enabled ?? false;
  const showPickupScheduling = windowConfig?.pickupSchedulingEnabled ?? false;
  const pickupAvailableDays = windowConfig?.pickupAvailableDays ?? 62;
  const pickupBlackoutDates = windowConfig?.pickupBlackoutDates ?? [];
  const pickupMaxLeadDays = windowConfig?.pickupMaxLeadDays ?? 7;
  const deliveryEnabled = windowConfig?.deliveryEnabled ?? true;
  const pickupEnabled = windowConfig?.pickupEnabled ?? false;
  const showFulfillmentSelector = deliveryEnabled && pickupEnabled;

  useEffect(() => {
    if (deliveryEnabled && !pickupEnabled) setFulfillmentType("delivery");
    else if (!deliveryEnabled && pickupEnabled) setFulfillmentType("pickup");
  }, [deliveryEnabled, pickupEnabled]);

  const fulfillmentTypeResolved = windowConfig
    ? (deliveryEnabled && !pickupEnabled ? "delivery" : !deliveryEnabled && pickupEnabled ? "pickup" : fulfillmentType)
    : fulfillmentType;

  useEffect(() => {
    if (!windowConfig) return;
    // Phase 2: only auto-seed a default date during INITIAL load. After init, every date/type
    // change goes through bindToDate / changeDateKeepCart (the binding mutators), so this effect
    // must not fire and yank the active date out from under a user-driven binding.
    if (isInitialized) return;
    const resolvedType = deliveryEnabled && !pickupEnabled ? "delivery" : !deliveryEnabled && pickupEnabled ? "pickup" : fulfillmentType;
    const dates = getAvailableFulfillmentDates(resolvedType, windowConfig);
    // Seed dates[0] ONLY when no valid date is chosen for this method. NEVER relocate a date that
    // is already valid. The previous `typeChanged ? "" : fulfillmentDate` reset blanked a valid
    // date whenever the method toggled, snapping it to the earliest day — and because
    // isInitialized briefly flickers back to false while the account re-resolves on load/navigation
    // (see the account-switch effect below), that reset fired on a real user method toggle and
    // relocated a second order's date (the two-date "switch pickup→delivery jumps July 2 → June 30"
    // bug, org demo). Guarding on the date's own validity makes a same-day method toggle a no-op here.
    const currentValid = !!fulfillmentDate && dates.some(d => d.value === fulfillmentDate);
    if (currentValid) return;
    if (dates.length > 0) {
      setFulfillmentDate(dates[0].value);
      setPickupDate(resolvedType === "pickup" ? dates[0].value : "");
    } else {
      setFulfillmentDate("");
      setPickupDate("");
    }
  }, [windowConfig, fulfillmentType, fulfillmentDate, deliveryEnabled, pickupEnabled, isInitialized]);

  // Cart form fields are keyed by PRODUCT ID — never by position in the `products` list.
  // The menu refetches on window focus (5-min staleTime) and an admin can add, reorder,
  // re-categorize or deactivate a product at any time, so the list can shift under a live
  // cart. A positional key then silently re-attaches every typed quantity to the neighbouring
  // product: the customer sees lines they never picked and their intended lines drop to zero
  // (CNP, 2026-09-02 — two customers checked out with "auto-added" items).
  const productKey = (productId: number): string => `product_${productId}`;
  const productIdFromKey = (key: string): number => parseInt(key.replace('product_', ''), 10);

  // Helper function to create safe keys for form fields (server-side quantities are keyed by name)
  const createSafeKey = (productName: string): string => {
    const product = products.find(p => p.name === productName);
    return product ? productKey(product.id) : 'product_-1';
  };

  // Helper function to get product name from safe key
  const getProductFromKey = (key: string): string => {
    const id = productIdFromKey(key);
    return products.find(p => p.id === id)?.name || '';
  };

  // Fetch account by custom URL or skip if /customer route.
  //
  // The cache key carries orgSlug because a custom URL is unique only WITHIN an
  // org, not across orgs: "cn-produce" is one account at Gomes and a different
  // account at Big P. A buyer who orders from both vendors in one app session
  // (one long-lived WKWebView on the iPhone shell, cache held until force-quit)
  // would otherwise read the first vendor's cached account on the second
  // vendor's page — staleTime is Infinity, so it never refetches — and the
  // account-binding effect below, seeing the cached account differ from the
  // signed-in one, logs them out back to the phone screen. (Chris @ CN Produce,
  // signed in to Big P, 2026-10-08.) An explicit queryFn keeps the fetched path
  // (org-scoped by getApiPath) independent of the cache key.
  const { data: fetchedAccount, isLoading: accountLoading } = useQuery<Account>({
    queryKey: ["/api/accounts/customUrl", orgSlug, customUrl],
    queryFn: async () => (await apiRequest("GET", `/api/accounts/customUrl/${customUrl}`)).json(),
    enabled: Boolean(isCustomUrlRoute && customUrl),
  });

  // Fetch sibling accounts for group switching
  const { data: siblingsData } = useQuery<{ siblings: { id: number; name: string; customUrl: string; isCurrent: boolean }[] }>({
    queryKey: ["/api/customer/siblings", account?.id, orgSlug],
    queryFn: async () => {
      const res = await apiRequest("POST", `/api/customer/siblings`, {
        accountId: account!.id
      });
      return res.json();
    },
    enabled: Boolean(account?.id && account?.parentAccountId && orgSlug),
  });
  const siblingAccounts = siblingsData?.siblings ?? [];
  const hasGroupSiblings = siblingAccounts.length > 1;

  // Fetch current period orders for this account.
  // Response shape is { orders, prevQuantities }; prevQuantities is consumed in Part 2.
  //
  // The cart is seeded ONCE from this list, and a submit REPLACES the order on its day with
  // the cart — so the seed must come from the server as it is now, never from a cached copy.
  // Refetch on every mount and seed only from that read once it SUCCEEDED (lib/cartSeed.ts): a cached
  // list from before the last submit seeded Back-from-the-summary with the old lines, and the
  // next submit replaced the order with them (Gomes, 2026-10-25/26;
  // tests/client/submit-refetch-survives-unmount.test.ts).
  const pageMountedAt = useRef(Date.now()).current;
  const {
    data: currentPeriodResponse,
    isLoading: ordersLoading,
    isFetching: ordersFetching,
    isError: ordersError,
    dataUpdatedAt: ordersUpdatedAt,
    refetch: refetchOrders,
  } = useQuery<{
    orders: OrderWithDetails[];
    prevQuantities: Record<number, number>;
  }>({
    queryKey: ["/api/accounts", account?.id, "orders", "current", orgSlug],
    queryFn: readPath(`/api/accounts/${account?.id}/orders/current`),
    enabled: Boolean(account?.id),
    // refetchOnMount 'always' + staleTime 0 (lib/cartSeed.ts): the query starts disabled
    // before the account resolves, so only a stale query fetches when its key switches.
    ...ORDERS_CURRENT_OPTIONS,
  });
  const currentPeriodOrders = useMemo(
    () => currentPeriodResponse?.orders ?? [],
    [currentPeriodResponse],
  );
  const prevQuantities = currentPeriodResponse?.prevQuantities ?? {};

  // ---- Phase 2: per-date order binding ----------------------------------------------------
  // ONE ORDER PER DATE (regardless of delivery/pickup) — this matches the server, which keys
  // consolidation by fulfillment DATE only. So the cart binds to whatever order is on the active
  // date; an active date with no order means "composing a new order". Method is just a property of
  // that order. Keying by date+method here would let the client think two same-date orders are
  // distinct while the server overwrites one by date — the cannibalization bug. See CLAUDE.md.
  const activeFulfillmentDate = fulfillmentDate || pickupDate || "";
  const boundOrder = useMemo(() => {
    if (!activeFulfillmentDate) return null;
    return currentPeriodOrders.find(o =>
      (o.fulfillmentDate || o.pickupDate) === activeFulfillmentDate
    ) || null;
  }, [currentPeriodOrders, activeFulfillmentDate]);
  const editingOrderId = boundOrder?.id ?? null;

  // Ref mirror of the active date key so the debounced auto-save (which captures by closure)
  // always writes under the CURRENT date, not the date that was active when it subscribed.
  const activeDateRef = useRef('');
  activeDateRef.current = cartDateKey(fulfillmentDate, pickupDate, fulfillmentTypeResolved);
  // True for one tick while bindToDate/startNewOrder reset the form, so the auto-save watcher
  // doesn't treat the programmatic reset as a user edit.
  const isBindingRef = useRef(false);

  const blankQuantities = (): Record<string, number> => {
    const q: Record<string, number> = {};
    products.forEach((p) => { q[productKey(p.id)] = 0; });
    return q;
  };

  // Seed cart quantities from a saved-cart snapshot (keyed by productId string).
  const applySavedToQuantities = (q: Record<string, number>, saved: { quantities: Record<string, number> }) => {
    Object.entries(saved.quantities).forEach(([productId, quantity]) => {
      const product = products.find(p => p.id.toString() === productId);
      if (product) q[productKey(product.id)] = quantity;
    });
  };

  // Seed cart quantities from an order's line items, matched by product id — a product
  // renamed since the order was placed is still the same product (OW-1).
  const applyOrderToQuantities = (q: Record<string, number>, order: OrderWithDetails) => {
    order.items.forEach(item => {
      const product = products.find(p => p.id === item.productId);
      if (product) {
        const k = productKey(product.id);
        q[k] = (q[k] || 0) + item.quantity;
      }
    });
  };

  // Serialize current form values into a {quantities: byProductId, notes} snapshot.
  // Mirrors the mapping in the auto-save effect so flush + debounced save agree.
  const snapshotFromForm = (): { quantities: Record<string, number>, notes: string } => {
    const values = form.getValues();
    const byProductId: Record<string, number> = {};
    Object.entries(values.quantities || {}).forEach(([key, quantity]) => {
      const qty = (quantity as number) ?? 0;
      if (qty > 0) {
        const id = productIdFromKey(key);
        if (products.some(p => p.id === id)) byProductId[id.toString()] = qty;
      }
    });
    return { quantities: byProductId, notes: (values.notes || '').trim() };
  };

  // Synchronously persist the current cart under its OWN date key before switching dates,
  // cancelling any in-flight debounced save so a stale snapshot can't land under the new key.
  const flushCurrentCart = () => {
    if (!account) return;
    if (saveTimeoutRef.current) { clearTimeout(saveTimeoutRef.current); saveTimeoutRef.current = null; }
    const snapshot = snapshotFromForm();
    const key = activeDateRef.current;
    if (Object.keys(snapshot.quantities).length > 0 || snapshot.notes) {
      saveCartToStorage(account.id, key, snapshot);
    } else {
      clearCartFromStorage(account.id, key);
    }
    lastSavedSnapshotRef.current = snapshot;
  };

  const firstAvailableDate = (type: 'delivery' | 'pickup'): string => {
    if (!windowConfig) return "";
    const dates = getAvailableFulfillmentDates(type, windowConfig);
    return dates[0]?.value || "";
  };

  // When switching method, keep the current day if it's valid for the new method (so toggling
  // delivery/pickup on an order is an in-place method change, not a surprise move); else the first
  // available day for that method.
  const dateForType = (type: 'delivery' | 'pickup'): string => {
    if (!windowConfig) return "";
    const dates = getAvailableFulfillmentDates(type, windowConfig);
    if (activeFulfillmentDate && dates.some(d => d.value === activeFulfillmentDate)) return activeFulfillmentDate;
    return dates[0]?.value || "";
  };

  // The single re-bind path: load the cart for (dateStr, type) — saved draft wins, else that
  // date's existing order, else empty — and reset all binding state coherently.
  const bindToDate = (dateStr: string, type: 'delivery' | 'pickup') => {
    if (!account) return;
    if (moveCancelOrderId != null) {
      // Leaving an in-progress move: the carried cart is a transient copy of the order being moved,
      // so DISCARD it instead of flushing — otherwise it persists as a phantom draft order on the
      // move's target date (a "ghost" that later resurfaces in the switcher).
      clearCartFromStorage(account.id, activeDateRef.current);
    } else {
      flushCurrentCart();
    }
    isBindingRef.current = true;
    setFulfillmentType(type);
    setFulfillmentDate(dateStr);
    setPickupDate(type === 'pickup' ? dateStr : "");
    const key = cartDateKey(dateStr, type === 'pickup' ? dateStr : "", type);
    const target = currentPeriodOrders.find(o =>
      (o.fulfillmentDate || o.pickupDate) === dateStr &&
      ((o.fulfillmentType || 'delivery') === type)
    ) || null;
    const saved = normalizeSavedCart(loadCartFromStorage(account.id, key));
    const q = blankQuantities();
    if (saved) applySavedToQuantities(q, saved);
    else if (target) applyOrderToQuantities(q, target);
    form.reset({ phone: account.phone || "", quantities: q, notes: saved?.notes ?? target?.notes ?? "" });
    cartTrail.seeded({ trigger: "date_switch", source: saved ? "draft" : target ? "order" : "empty", quantities: q, dateKey: key, orderId: target?.id });
    lastSavedSnapshotRef.current = saved ?? null;
    submissionId.current = crypto.randomUUID();
    setMoveCancelOrderId(null);
    setMoveCancelOrderFromKey(null);
    setTimeout(() => { isBindingRef.current = false; }, 0);
  };

  // "+ Order for another date": clear to an empty, date-unset cart so the customer picks a NEW
  // date. boundOrder becomes null ⇒ the editor reads "New order" and submit posts a fresh dated
  // order (the Phase-1 server creates it without touching other dates).
  const startNewOrder = () => {
    if (!account) return;
    flushCurrentCart();
    isBindingRef.current = true;
    setFulfillmentDate("");
    setPickupDate("");
    const blank = blankQuantities();
    form.reset({ phone: account.phone || "", quantities: blank, notes: "" });
    cartTrail.seeded({ trigger: "new_order", source: "empty", quantities: blank, dateKey: cartDateKey("", "", fulfillmentTypeResolved) });
    lastSavedSnapshotRef.current = null;
    submissionId.current = crypto.randomUUID();
    setMoveCancelOrderId(null);
    setMoveCancelOrderFromKey(null);
    setTimeout(() => { isBindingRef.current = false; }, 0);
  };

  // Move (recreate model): change the active day/method but KEEP the current cart (the items being
  // moved). On submit the Phase-1 server creates the order at the new date; we then cancel the
  // original (moveCancelOrderId). Does NOT re-seed — the cart carries over.
  const changeDateKeepCart = (dateStr: string, type: 'delivery' | 'pickup') => {
    if (saveTimeoutRef.current) { clearTimeout(saveTimeoutRef.current); saveTimeoutRef.current = null; }
    cartTrail.dateCarried(cartDateKey(dateStr, type === 'pickup' ? dateStr : "", type));
    setFulfillmentType(type);
    setFulfillmentDate(dateStr);
    setPickupDate(type === 'pickup' ? dateStr : "");
    submissionId.current = crypto.randomUUID();
  };

  // Cancel an in-progress move and return to editing the original order, discarding the move cart so
  // it doesn't linger as a phantom draft. Used when the customer toggles back to the order's own
  // method, or re-picks its original date, mid-move.
  const cancelMoveTo = (order: OrderWithDetails) => {
    // bindToDate discards the in-progress move cart (moveCancelOrderId is still set here) and re-binds
    // to the original order, clearing the move.
    const oType = (order.fulfillmentType || 'delivery') as 'delivery' | 'pickup';
    bindToDate(order.fulfillmentDate || order.pickupDate || '', oType);
  };

  // Single entry point for any date/method pick from the picker. Editing an existing order ⇒ ask
  // intent (move vs separate). Mid-move ⇒ re-target (carry). Composing new ⇒ re-seed per-date.
  const requestDateChange = (dateStr: string, type: 'delivery' | 'pickup') => {
    if (!dateStr) return;
    if (dateStr === fulfillmentDate && type === fulfillmentType) return;
    if (boundOrder) {
      const boundDate = boundOrder.fulfillmentDate || boundOrder.pickupDate || '';
      if (dateStr !== boundDate) {
        // DATE changed ⇒ a move: ask intent (move this order vs start a separate one).
        setPendingMove({ date: dateStr, type });
      } else {
        // Same DATE, only the METHOD changed ⇒ in-place edit of this order. The server keys by
        // date, so submitting updates this same order's method. No recreate, no prompt.
        changeDateKeepCart(dateStr, type);
      }
    } else if (moveCancelOrderId != null) {
      // Mid-move: re-picking the order's ORIGINAL date cancels the move and edits it in place again
      // (so the customer isn't trapped after a move). Any other date re-targets the move.
      const moving = currentPeriodOrders.find(o => o.id === moveCancelOrderId);
      const movingDate = moving ? (moving.fulfillmentDate || moving.pickupDate || '') : '';
      if (moving && dateStr === movingDate) {
        cancelMoveTo(moving);
      } else {
        changeDateKeepCart(dateStr, type);
      }
    } else if (totalItems > 0) {
      // Composing a new (unsubmitted) order that already has items: changing its day OR method must
      // CARRY those items, not re-seed an empty cart (which silently strands them under the old
      // slot's draft key and drops the cart to the "start a new order" empty state). Clear the old
      // slot so its draft can't linger as a phantom order in the switcher. Mirrors selectNewDay.
      if (account) clearCartFromStorage(account.id, activeDateRef.current);
      changeDateKeepCart(dateStr, type);
    } else {
      bindToDate(dateStr, type);
    }
  };

  // Fulfillment-method toggle. Delivery and pickup can run on different days, so a method switch may
  // not be possible on the order's current day. If it IS (same day supports both), switch in place.
  // For a SUBMITTED order whose day can't do the new method, do NOT silently bump it to another date
  // (that surprised customers and trapped them mid-move) — keep it put and explain. A composing
  // (unsubmitted) order is free to move, since there's no original order left behind.
  const requestMethod = (type: 'delivery' | 'pickup') => {
    if (type === fulfillmentType) return; // already on this method
    const target = dateForType(type);
    const sameDay = !!target && target === activeFulfillmentDate;
    if (boundOrder && !sameDay) {
      toast({
        title: `${type === 'pickup' ? 'Pickup' : 'Delivery'} isn't available on ${formatChipDate(activeFulfillmentDate)}`,
        description: `This order stays on ${formatChipDate(activeFulfillmentDate)}. Start a new order for another day to use ${type === 'pickup' ? 'pickup' : 'delivery'}.`,
      });
      return;
    }
    requestDateChange(target, type);
  };

  // Inline constraint for the method toggle: a SUBMITTED order can only switch method when the other
  // method's schedule also covers the order's current day (otherwise requestMethod refuses, to avoid
  // silently relocating it). Returns the reason to show on the disabled tile, else null. Mirrors the
  // requestMethod block condition exactly, so the tile is disabled iff a tap would have been blocked.
  // Composing (unsubmitted) carts are free to move days, so they never block.
  const methodUnavailableReason = (type: 'delivery' | 'pickup'): string | null => {
    if (!boundOrder || type === fulfillmentType) return null;
    const target = dateForType(type);
    if (target && target === activeFulfillmentDate) return null; // same day supports both methods
    // Compact date (no weekday) keeps the reason on one short line inside the ~140px tile; any wrap
    // breaks cleanly before the date rather than stranding the day number.
    const d = activeFulfillmentDate
      ? new Date(activeFulfillmentDate + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
      : '';
    return `Not available ${d}`.trim();
  };

  // ---- Phase 2: order-switcher helpers ----------------------------------------------------
  // Short chip label, e.g. "Tue Jun 23". Parse at local noon so the YYYY-MM-DD never drifts a day.
  const formatChipDate = (dateStr: string): string => {
    if (!dateStr) return '';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };
  const formatDayName = (dateStr: string): string => {
    if (!dateStr) return 'this';
    return new Date(dateStr + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });
  };

  // True when ANOTHER of the customer's orders (not the one being edited) already occupies this
  // DATE (any method) — disabled in the picker so a date can never hold two orders, which the
  // server would silently overwrite. Excludes the bound order (so editing keeps its own day) AND the
  // order currently being moved (so its original day stays selectable to cancel the move, instead of
  // greying out and trapping the customer). The `type` arg is call-site symmetry, not part of the key.
  const isDateTakenByOther = (dateStr: string, _type?: 'delivery' | 'pickup'): boolean =>
    currentPeriodOrders.some(o =>
      o.id !== (boundOrder?.id ?? -1) &&
      o.id !== moveCancelOrderId &&
      (o.fulfillmentDate || o.pickupDate) === dateStr);

  // Orders for the switcher, soonest day first.
  const sortedOrders = useMemo(() =>
    [...currentPeriodOrders].sort((a, b) =>
      (a.fulfillmentDate || a.pickupDate || '').localeCompare(b.fulfillmentDate || b.pickupDate || '')),
    [currentPeriodOrders]);

  // Fetch recent orders for past order display in bottom bar — deferred until orders/current finishes
  const { data: recentOrders = [], isFetched: recentOrdersFetched } = useQuery<OrderWithDetails[]>({
    queryKey: ["/api/accounts", account?.id, "orders", orgSlug],
    queryFn: readPath(`/api/accounts/${account?.id}/orders`),
    enabled: Boolean(account?.id) && !ordersLoading,
  });

  // Initialize form: bind the cart to ONE existing order (earliest upcoming) or an empty cart.
  // Phase 2: no longer merges every order into one cart. Guarded by !isInitialized so a
  // mid-session refetch (e.g. after submit invalidates orders/current) never clobbers in-progress
  // edits — isInitialized resets only on account change and onSuccess (which then navigates away).
  useEffect(() => {
    if (isInitialized) return;
    if (!(products.length > 0 && account && !productsLoading && !ordersLoading)) return;
    // Only from a read that succeeded after this page mounted (lib/cartSeed.ts): a list being
    // refetched, or left in place by a failed refetch, may predate the last submit.
    if (!ordersReadyToSeed({ isFetching: ordersFetching, isError: ordersError, dataUpdatedAt: ordersUpdatedAt }, pageMountedAt)) return;

    // Housekeeping: drop only already-expired per-date carts (never live drafts).
    sweepExpiredCartKeys(account.id);

    // Choose the initial bound order: the earliest-upcoming editable order (soonest date), so a
    // returning customer lands on their next order rather than a blank editor.
    const sorted = [...currentPeriodOrders].sort((a, b) =>
      (a.fulfillmentDate || a.pickupDate || '').localeCompare(b.fulfillmentDate || b.pickupDate || ''));
    let initial = sorted[0] || null;
    let initialPick: 'earliest' | 'return_to' | 'requested' | 'none' = initial ? 'earliest' : 'none';

    // If the customer just submitted an order and pressed Back, return them to THAT order
    // (set in onSuccess), not the earliest. One-shot: consume and clear the marker.
    try {
      const retRaw = sessionStorage.getItem(`fo_return_to_${account.id}`);
      if (retRaw) {
        sessionStorage.removeItem(`fo_return_to_${account.id}`);
        const ret = JSON.parse(retRaw);
        const match = currentPeriodOrders.find(o =>
          (o.fulfillmentDate || o.pickupDate) === ret.date &&
          ((o.fulfillmentType || 'delivery') === ret.type));
        if (match) { initial = match; initialPick = 'return_to'; }
      }
    } catch {}

    // "Change this order" on the order summary (?order=<id>): open THAT order. Only a
    // hint — it wins only when the order is one of this account's open orders, which
    // orders/current already scopes to the signed-in session and org. One-shot: the
    // param leaves the address so a reload opens the usual order.
    const requestedId = requestedOrderId(window.location.search);
    if (requestedId != null) {
      const match = currentPeriodOrders.find(o => o.id === requestedId);
      if (match) { initial = match; initialPick = 'requested'; }
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('order');
        window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
      } catch {}
    }

    // No existing order ⇒ the default date comes from firstAvailableDate(windowConfig). If the
    // config query hasn't resolved yet, WAIT (don't flip isInitialized) — otherwise we'd seed a
    // dateless cart and the now-isInitialized-gated auto-select effect could never set a default.
    // Once config settles (truthy, or null for a no-windows org) this effect re-runs and proceeds.
    if (!initial && !windowConfig && windowConfigLoading) return;

    // Resolve the initial active date in BOTH cases so the cart key, the picker, and boundOrder
    // all agree: the bound order's date when one exists, else the first available date (a fresh
    // new order). This makes the restore effect authoritative over the pre-init auto-select effect.
    let initialType: 'delivery' | 'pickup' = fulfillmentTypeResolved;
    let initialDate = "";
    let initialPickup = "";
    if (initial) {
      initialType = ((initial.fulfillmentType || 'delivery') as 'delivery' | 'pickup');
      initialDate = initial.fulfillmentDate || initial.pickupDate || "";
      initialPickup = initial.pickupDate || (initialType === 'pickup' ? initialDate : "");
    } else {
      initialDate = firstAvailableDate(initialType);
      initialPickup = initialType === 'pickup' ? initialDate : "";
    }
    if (initialDate) {
      setFulfillmentType(initialType);
      setFulfillmentDate(initialDate);
      setPickupDate(initialPickup);
    }

    // Seed cart for the initial date: a saved draft wins (unsaved work); else one-time migrate the
    // pre-Phase-2 un-suffixed key (only if still fresh); else the order's own items; else empty.
    const key = cartDateKey(initialDate, initialPickup, initialType);
    let saved = normalizeSavedCart(loadCartFromStorage(account.id, key));
    let savedFromLegacy = false;
    if (!saved) {
      const legacyRaw = localStorage.getItem(getLegacyStorageKey(account.id));
      if (legacyRaw) {
        try {
          const legacy = JSON.parse(legacyRaw);
          const ONE_DAY = 24 * 60 * 60 * 1000;
          if (legacy.timestamp && Date.now() - legacy.timestamp <= ONE_DAY) {
            saved = normalizeSavedCart(legacy);
            savedFromLegacy = !!saved;
          }
        } catch { saved = null; }
        localStorage.removeItem(getLegacyStorageKey(account.id));
      }
    }

    const q = blankQuantities();
    if (saved) applySavedToQuantities(q, saved);
    else if (initial) applyOrderToQuantities(q, initial);

    if (saved) {
      lastSavedSnapshotRef.current = { quantities: saved.quantities, notes: saved.notes };
    }

    form.reset({
      phone: account.phone || "",
      quantities: q,
      notes: saved?.notes ?? initial?.notes ?? "",
    });
    cartTrail.seeded({
      trigger: "seed",
      source: saved ? (savedFromLegacy ? "legacy_draft" : "draft") : initial ? "order" : "empty",
      quantities: q,
      dateKey: key,
      orderId: initial?.id,
      initialPick,
    });

    setIsInitialized(true);
  }, [products, account, currentPeriodOrders, productsLoading, ordersLoading, ordersFetching, ordersError, ordersUpdatedAt, isInitialized, windowConfig, windowConfigLoading]);
  
  // Auto-save cart to localStorage when form changes
  useEffect(() => {
    if (!isInitialized || !account || !products.length) return;
    
    // Subscribe to form changes
    const subscription = form.watch((value) => {
      // Skip the programmatic form.reset done by bindToDate/startNewOrder — not a user edit.
      if (isBindingRef.current) return;

      const watchedQuantities = value.quantities || {};
      const watchedNotes = value.notes || '';

      // Clear existing timeout
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      // Debounce the save operation
      saveTimeoutRef.current = setTimeout(() => {
        const quantitiesByProductId: Record<string, number> = {};
        Object.entries(watchedQuantities || {}).forEach(([key, quantity]) => {
          const qty = quantity ?? 0;
          if (qty > 0) {
            const id = productIdFromKey(key);
            if (products.some(p => p.id === id)) {
              quantitiesByProductId[id.toString()] = qty;
            }
          }
        });

        const currentSnapshot = {
          quantities: quantitiesByProductId,
          notes: watchedNotes || ""
        };

        // Check if current snapshot matches last saved - don't save duplicates
        const lastSnapshot = lastSavedSnapshotRef.current;
        const isSameAsLast = lastSnapshot &&
          JSON.stringify(currentSnapshot.quantities) === JSON.stringify(lastSnapshot.quantities) &&
          currentSnapshot.notes === lastSnapshot.notes;

        if (isSameAsLast) {
          return;
        }

        // Don't overwrite good data with empty cart on initial load
        const isEmpty = Object.keys(quantitiesByProductId).length === 0 && !watchedNotes;
        const hadData = lastSnapshot && (Object.keys(lastSnapshot.quantities).length > 0 || lastSnapshot.notes);

        if (isEmpty && hadData) {
          Sentry.addBreadcrumb({ category: "order", message: "Cart save skipped: empty would overwrite non-empty", level: "warning" });
          return;
        }

        // Save if there are items in the cart — keyed by the CURRENT active date (per-date cart).
        if (Object.keys(quantitiesByProductId).length > 0 || watchedNotes) {
          saveCartToStorage(account.id, activeDateRef.current, currentSnapshot);
          lastSavedSnapshotRef.current = currentSnapshot;
        } else {
          clearCartFromStorage(account.id, activeDateRef.current);
          lastSavedSnapshotRef.current = null;
        }
      }, 600); // 600ms debounce
    });
    
    return () => {
      subscription.unsubscribe();
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [account, products, isInitialized]);

  // Cart breadcrumbs: every quantity edit (a +/−, a typed quantity, the drawer, a size
  // card) lands here through the form. A reset (no field name) only re-baselines the diff;
  // the code that reset the form says what it did.
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (!name) { cartTrail.resync(value.quantities ?? {}); return; }
      if (!name.startsWith('quantities.')) return;
      cartTrail.quantitiesChanged(value.quantities ?? {}, activeDateRef.current);
    });
    return () => subscription.unsubscribe();
  }, [form, cartTrail]);
  
  // Reset binding state ONLY on a genuine switch to a DIFFERENT account (group sub-account switch) —
  // never on the initial undefined→id resolve or a transient account re-read. `account` momentarily
  // re-resolves to null during load/navigation; the old unconditional reset on every `account?.id`
  // change flickered isInitialized back to false mid-session, which re-enabled the pre-init
  // auto-select effect and let it relocate the active date (the two-date pickup→delivery jump).
  // null/undefined ids never trigger a reset; the first real id only records the baseline.
  const lastInitedAccountIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (account?.id == null) return;
    if (lastInitedAccountIdRef.current === account.id) return;
    const previousAccountId = lastInitedAccountIdRef.current;
    const isAccountSwitch = previousAccountId !== null;
    lastInitedAccountIdRef.current = account.id;
    if (previousAccountId !== null) cartTrail.accountSwitched(previousAccountId, account.id);
    clearLegacyStorageKeys(account.id);
    if (isAccountSwitch) {
      setIsInitialized(false);
      lastSavedSnapshotRef.current = null;
    }
  }, [account?.id]);

  // Demo welcome lightbox: show once per session for demo orgs
  useEffect(() => {
    if (organization?.isDemo && !sessionStorage.getItem('demo_welcome_seen')) {
      setShowDemoWelcome(true);
    }
  }, [organization?.isDemo]);





  // Account binding (2026-09-17 wrong-account order): the page's account comes from
  // the PUBLIC customUrl lookup, but the server writes orders to the SESSION's
  // account. A leftover session for account A on account B's URL (admin "open
  // ordering page" link, reminder link, bookmark, sibling-switch race) used to
  // render B and submit into A. Only proceed once the two agree:
  //   - grouped siblings → move the session via /customer/switch-account
  //   - anything else    → drop the other session and show the sign-in card
  const accountBindingRef = useRef(false);
  useEffect(() => {
    if (!isCustomUrlRoute || sessionAccountId == null || accountLoading) return;
    // Unknown URL: fall through to the 404 page (no account, so no form).
    if (!fetchedAccount || fetchedAccount.id === sessionAccountId) {
      setSessionVerified(true);
      setSessionChecking(false);
      return;
    }
    if (accountBindingRef.current) return;
    accountBindingRef.current = true;
    const target = fetchedAccount;
    const loginPath = orgSlug ? signInHref(orgSlug, { order: requestedOrderId(window.location.search) }) : "/";
    (async () => {
      if (target.parentAccountId) {
        try {
          await apiRequest("POST", "/api/customer/switch-account", {
            currentAccountId: sessionAccountId,
            targetAccountId: target.id,
          });
          // Re-verify rather than trust the switch: only render once the cookie
          // the browser will actually send names the displayed account.
          const verify = await (await apiRequest("POST", "/api/customer/verify-session", {})).json();
          if (verify.valid && verify.accountId === target.id) {
            accountBindingRef.current = false;
            setSessionAccountId(verify.accountId);
            return;
          }
        } catch {
          // Not siblings (403) or the switch failed — fall through to sign-in.
        }
      }
      await apiRequest("POST", "/api/customer/logout", {}).catch(() => {});
      forgetCustomerDataOnLeave(orgSlug);
      setIsRedirecting(true);
      setSessionChecking(false);
      setLocation(loginPath);
    })();
  }, [isCustomUrlRoute, sessionAccountId, accountLoading, fetchedAccount, orgSlug, setLocation]);

  // Update account when fetched — only once the session is confirmed to own it.
  useEffect(() => {
    if (isCustomUrlRoute && (sessionAccountId == null || fetchedAccount?.id !== sessionAccountId)) return;
    if (fetchedAccount && fetchedAccount.isActive) {
      setAccount(fetchedAccount);
      form.setValue("phone", fetchedAccount.phone || "");
      // Initialize notification settings from account data
      setNotificationSettings({
        reminderEnabled: fetchedAccount.reminderEnabled || false,
        emailNotificationEnabled: fetchedAccount.emailNotificationEnabled || false,
        smsOrderReceipts: fetchedAccount.smsOrderReceipts || false,
        emailOrderReceipts: fetchedAccount.emailOrderReceipts || false
      });
    }
  }, [fetchedAccount, form, isCustomUrlRoute, sessionAccountId]);

  // Update notification preferences mutation — the flipped switch's own column and
  // nothing else: every other notification column on the account stays as it is, and
  // one that is unset keeps following the org.
  const updateNotificationMutation = useMutation({
    mutationFn: async ({ key, value }: { key: BellSwitch; value: boolean }) => {
      if (!account?.id) throw new Error('No account selected');

      try {
        const { path, body } = bellToggleRequest(account.id, key, value);
        await apiRequest('PATCH', path, body);
      } catch (error) {
        // Revert optimistic update on error
        setNotificationSettings({
          reminderEnabled: account.reminderEnabled || false,
          emailNotificationEnabled: account.emailNotificationEnabled || false,
          smsOrderReceipts: account.smsOrderReceipts || false,
          emailOrderReceipts: account.emailOrderReceipts || false
        });
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/accounts/customUrl", orgSlug, customUrl] });
      // Don't show toast or close popup - let users adjust multiple settings
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to update notification settings.",
        variant: "destructive"
      });
    }
  });

  // Email verification mutation
  const requestEmailVerificationMutation = useMutation({
    mutationFn: async (data: { accountId: number; email: string; enableFeature: string }) => {
      const response = await apiRequest("POST", "/api/customer/request-email-verification", data);
      return response.json();
    },
    onSuccess: () => {
      setEmailVerificationSent(true);
      toast({
        title: "Verification email sent!",
        description: "Please check your inbox and click the verification link.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to send verification email.",
        variant: "destructive"
      });
    }
  });

  const handleNotificationToggle = (key: keyof typeof notificationSettings, value: boolean) => {
    // Check if trying to enable email features without an email address
    if (value && (key === 'emailNotificationEnabled' || key === 'emailOrderReceipts')) {
      if (!account?.emailAddress) {
        // Show email verification dialog
        setPendingEmailFeature(key);
        setEmailToVerify("");
        setEmailVerificationSent(false);
        setShowEmailVerificationDialog(true);
        return; // Don't toggle yet - wait for email verification
      }
    }
    
    // Optimistic update
    setNotificationSettings({ ...notificationSettings, [key]: value });
    updateNotificationMutation.mutate({ key, value });
  };
  
  const handleSubmitEmailVerification = () => {
    if (!account?.id || !emailToVerify || !pendingEmailFeature) return;
    
    // Basic client-side email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailToVerify)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive"
      });
      return;
    }
    
    requestEmailVerificationMutation.mutate({
      accountId: account.id,
      email: emailToVerify,
      enableFeature: pendingEmailFeature
    });
  };

  const handleSwitchAccount = async (targetId: number, targetCustomUrl: string) => {
    if (!account) return;
    try {
      const response = await apiRequest("POST", `/api/customer/switch-account`, {
        currentAccountId: account.id,
        targetAccountId: targetId
      });
      if (response.ok) {
        const { customUrl: newUrl } = await response.json();
        setAccountSwitcherOpen(false);
        const redirectPath = orgSlug
          ? `/org/${orgSlug}/order/${newUrl}`
          : `/${newUrl}`;
        // Wait for browser to process Set-Cookie header before navigating.
        // The cookie race condition caused order overwrites on grouped accounts
        // (Garden Hill orders 1197, 1314). setTimeout yields to the browser's
        // network stack to finalize header processing before navigation.
        setTimeout(() => {
          window.location.href = redirectPath;
        }, 100);
      } else {
        const err = await response.json();
        toast({ title: "Error", description: err.message || "Unable to switch account.", variant: "destructive" });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Unable to switch account. Please try again.",
        variant: "destructive"
      });
    }
  };

  // Soft-cancel an order the customer owns (Phase 2b). Used by the standalone "Cancel order"
  // action and to retire the original order after a move (recreate model).
  const cancelOrder = async (orderId: number) => {
    const res = await apiRequest("POST", `/api/customer/order/${orderId}/cancel`, {});
    return res.json();
  };

  const cancelOrderMutation = useMutation({
    mutationFn: (order: OrderWithDetails) => cancelOrder(order.id),
    onSuccess: (_data, order) => {
      toast({
        title: "Order cancelled",
        action: (
          <ToastAction altText="Undo cancellation" onClick={() => recreateCancelledOrder(order)}>
            Undo
          </ToastAction>
        ),
      });
      setCancelConfirmOpen(false);
      // Clear the cancelled order's saved draft so its items don't linger as a ghost cart after
      // the list refreshes (the restore effect would otherwise reload that stale draft).
      if (account) {
        const d = order.fulfillmentDate || order.pickupDate || '';
        const type = (order.fulfillmentType || 'delivery') as 'delivery' | 'pickup';
        clearCartFromStorage(account.id, cartDateKey(d, type === 'pickup' ? d : '', type));
      }
      // Optimistically drop the cancelled order from the current-period cache BEFORE re-binding.
      // Otherwise the restore effect (triggered by setIsInitialized(false)) runs against the still
      // cached list, re-binds to the just-cancelled order, re-seeds its items into the form, and
      // flips isInitialized back to true — so the later refetch (now without it) is blocked by the
      // `if (isInitialized) return` guard and the cancelled order's items linger in the cart. Pruning
      // it here makes the effect re-bind against the correct post-cancel list (a remaining order, or
      // an empty cart when this was the last one).
      queryClient.setQueryData(
        ["/api/accounts", account?.id, "orders", "current", orgSlug],
        (old: any) => (old && Array.isArray(old.orders)
          ? { ...old, orders: old.orders.filter((o: any) => o.id !== order.id) }
          : old),
      );
      // Re-bind to a remaining order (or a fresh, empty new order if none) once state settles.
      setIsInitialized(false);
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", account?.id, "orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", account?.id, "orders", "current"] });
    },
    onError: (error: any) => {
      toast({ title: "Couldn't cancel order", description: error?.message || "Please try again.", variant: "destructive" });
    },
  });

  // Undo a customer cancel. The server cancel is a soft status flip, so the cancelled order is no
  // longer editable and a re-POST creates a fresh order on the same date with the same items
  // (per-date consolidation skips the cancelled one). New order id; the cancelled record stays put.
  // Quiet path — no navigation, unlike submitOrderMutation.
  const recreateCancelledOrder = async (order: OrderWithDetails) => {
    if (!account) return;
    try {
      // Lines go by product id (OW-1). Only what is on the menu now: a line the customer
      // cannot see (admin-added) was never theirs to re-send.
      const quantities: Record<string, number> = {};
      const items: Array<{ productId: number; quantity: number }> = [];
      order.items.forEach((it) => {
        const product = products.find(p => p.id === it.productId);
        if (!product || it.quantity <= 0) return;
        items.push({ productId: product.id, quantity: it.quantity });
        quantities[product.name] = (quantities[product.name] || 0) + it.quantity;
      });
      const fDate = order.fulfillmentDate || order.pickupDate || undefined;
      const type = (order.fulfillmentType || 'delivery') as 'delivery' | 'pickup';
      const res = await apiRequest("POST", "/api/orders", {
        phone: account.phone || "",
        quantities,
        items,
        notes: order.notes || "",
        fulfillmentType: type,
        fulfillmentDate: fDate,
        pickupDate: type === 'pickup' ? fDate : undefined,
        submissionId: crypto.randomUUID(),
        isKioskOrder: false,
        // The account this page shows — the server refuses (409) if the session is for another.
        accountId: account.id,
      });
      await res.json();
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", account.id, "orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", account.id, "orders", "current"] });
      // Take the customer back to the cart for the order they just restored (not whatever the list
      // re-bound to after the cancel). Seed the form from the order's own items — the refetched list
      // isn't visible to this closure yet — and open full-review; boundOrder resolves on the next
      // render once the refetch lands.
      if (fDate) {
        flushCurrentCart();
        isBindingRef.current = true;
        setFulfillmentType(type);
        setFulfillmentDate(fDate);
        setPickupDate(type === 'pickup' ? fDate : "");
        const q = blankQuantities();
        applyOrderToQuantities(q, order);
        form.reset({ phone: account.phone || "", quantities: q, notes: order.notes || "" });
        cartTrail.seeded({ trigger: "order_restored", source: "order", quantities: q, dateKey: cartDateKey(fDate, type === 'pickup' ? fDate : "", type), orderId: order.id });
        lastSavedSnapshotRef.current = null;
        submissionId.current = crypto.randomUUID();
        setMoveCancelOrderId(null);
        setCartDrawerMode('full-review');
        setTimeout(() => { isBindingRef.current = false; }, 0);
      }
      toast({ title: "Order restored" });
    } catch (error: any) {
      toast({ title: "Couldn't restore order", description: error?.message || "Please try again.", variant: "destructive" });
    }
  };

  // Discard an unsent draft order locally (no server call): clear its saved cart, then let the
  // restore effect re-bind to a remaining order (or an empty cart if none).
  const discardDraft = () => {
    // Capture the draft for Undo BEFORE clearing it: storage key, day/method, and items+notes.
    const undoSnapshot = snapshotFromForm();
    const undoKey = activeDateRef.current;
    const undoDate = fulfillmentDate || pickupDate || "";
    const undoType = fulfillmentTypeResolved;
    const canUndo = Object.keys(undoSnapshot.quantities).length > 0 || !!undoSnapshot.notes;

    setCancelConfirmOpen(false);
    if (account) clearCartFromStorage(account.id, activeDateRef.current);
    isBindingRef.current = true;
    setFulfillmentDate("");
    setPickupDate("");
    const blank = blankQuantities();
    form.reset({ phone: account?.phone || "", quantities: blank, notes: "" });
    cartTrail.seeded({ trigger: "discard", source: "empty", quantities: blank, dateKey: undoKey });
    lastSavedSnapshotRef.current = null;
    setMoveCancelOrderId(null);
    setIsInitialized(false);
    setTimeout(() => { isBindingRef.current = false; }, 0);
    toast({
      title: "Order discarded",
      action: canUndo ? (
        <ToastAction altText="Undo discard" onClick={() => restoreDiscardedDraft(undoKey, undoDate, undoType, undoSnapshot)}>
          Undo
        </ToastAction>
      ) : undefined,
    });
  };

  // Undo a discard: re-write the draft to its storage slot, then re-bind to it (bindToDate loads the
  // saved draft, which now wins) — reusing the binding state machine instead of hand-resetting state.
  const restoreDiscardedDraft = (
    key: string,
    date: string,
    type: 'delivery' | 'pickup',
    snapshot: { quantities: Record<string, number>, notes: string },
  ) => {
    if (!account) return;
    saveCartToStorage(account.id, key, snapshot);
    bindToDate(date, type);
  };

  // Submit order mutation
  const submitOrderMutation = useMutation({
    mutationFn: async (data: CustomerOrderInput) => {
      // Prevent double-submit (e.g. two rapid taps before isPending updates)
      if (isSubmittingOrder.current) return;
      isSubmittingOrder.current = true;
      // The order's lines, keyed by product id (OW-1). Everything in the cart goes —
      // including a product the menu has since lost, so the server can refuse it by
      // name instead of the line quietly disappearing here.
      const items: Array<{ productId: number; quantity: number }> = [];
      // Name-keyed copy, for a server that predates `items` (the moments of a deploy).
      const convertedQuantities: Record<string, number> = {};

      Object.entries(data.quantities).forEach(([key, quantity]) => {
        if (key.startsWith('product_')) {
          const productId = productIdFromKey(key);
          if (Number.isInteger(productId) && productId > 0 && quantity > 0) {
            items.push({ productId, quantity });
          }
          const productName = getProductFromKey(key);
          if (productName) {
            convertedQuantities[productName] = quantity;
          }
        } else {
          // Handle any legacy keys that might still be product names
          convertedQuantities[key] = quantity;
        }
      });
      
      const convertedData = {
        ...data,
        quantities: convertedQuantities,
        // No id-keyed lines at all means a cart of legacy name keys: let those speak.
        items: items.length > 0 ? items : undefined,
      };
      
      // Check if this is a kiosk order (URL param or localStorage)
      const isKioskOrder = isKioskMode();

      cartTrail.submitting(items, activeDateRef.current);

      try {
        const response = await apiRequest("POST", "/api/orders", {
          ...convertedData,
          isKioskOrder,
          pickupDate: pickupDate || fulfillmentDate || undefined,
          fulfillmentDate: fulfillmentDate || pickupDate || undefined,
          fulfillmentType: fulfillmentType || undefined,
          submissionId: submissionId.current,
          // True-move: ride the source order's id in the SAME request; the server
          // soft-cancels it after the target write succeeds (own-account only).
          moveFromOrderId: moveCancelOrderId ?? undefined,
          // The account this page shows. The server refuses with 409 ACCOUNT_MISMATCH
          // rather than writing to a different account the session belongs to.
          accountId: account?.id,
        });
        return response.json();
      } finally {
        isSubmittingOrder.current = false;
      }
    },
    onSuccess: (data: any) => {
      // Ship the cart's breadcrumb trail with one info event (lib/cartBreadcrumbs.ts).
      if (data?.id) cartTrail.submitted({ orderId: data.id, orgSlug, accountId: account?.id });

      // A sent order is the end of the first-order tips on this device.
      markTipsSeen(FIRST_ORDER_ONLY_TIPS);

      // Capture order ID from response for use in toast print link
      if (data?.id) {
        lastSubmittedOrderId.current = data.id;
      }

      // Remember which order was just submitted so that pressing Back on the order-summary page
      // returns the customer to THIS order (not the earliest one). Read in the restore effect.
      if (account?.id) {
        try {
          const retDate = data?.fulfillmentDate || fulfillmentDate || pickupDate || '';
          const retType = data?.fulfillmentType || fulfillmentType || 'delivery';
          sessionStorage.setItem(`fo_return_to_${account.id}`, JSON.stringify({ date: retDate, type: retType }));
        } catch {}
      }

      // Check if this was a kiosk order (URL param or localStorage)
      const isKioskOrder = isKioskMode();

      if (isKioskOrder && data?.id) {
        // Store order ID and show delivery location dialog
        setLastOrderId(data.id);
        setShowDeliveryLocationDialog(true);
      } else if (organization?.isDemo && data?.id) {
        // Demo org — skip toast, navigate directly to admin view
        track("demo_order_submitted", { demo_order_id: data.id });
        setDemoTransitioning(true);
        setLocation(`/demo/admin/order/${data.id}`);
      } else {
        // Regular order flow — navigate directly to order summary screen.
        // The order summary screen IS the confirmation, with a prominent Print
        // button and a Back button if the user wants to revise the order.
        // A ?submitted=1 query param triggers a green success banner there.
        const submittedOrderId = data.id;
        if (submittedOrderId && orgSlug && account?.id) {
          setLocation(`/org/${orgSlug}/order-summary/${account.id}/${submittedOrderId}?submitted=1`);
        }
      }
      
      // Clear saved cart from localStorage after successful submission (the submitted date's draft)
      if (account) {
        clearCartFromStorage(account.id, activeDateRef.current);
        // Disable auto-save temporarily to prevent re-saving cleared cart
        setIsInitialized(false);
      }
      
      // True-move: the server retired the moved-from order inside the submit request itself
      // (moveFromOrderId) — no second call to get lost. Clear the source date's local draft so
      // it can't resurface as a "Not sent yet" ghost in the switcher, then reset move state.
      if (moveCancelOrderId != null) {
        if (account && moveCancelOrderFromKey) {
          clearCartFromStorage(account.id, moveCancelOrderFromKey);
        }
        setMoveCancelOrderId(null);
        setMoveCancelOrderFromKey(null);
      }

      // Close cart drawer after successful submission and reset dirty flag
      setCartDrawerMode('closed');

      // Reset idempotency key so a future submission gets a fresh UUID
      submissionId.current = crypto.randomUUID();

      // Refresh queries to show updated data
      queryClient.invalidateQueries({ queryKey: ["/api/dashboard/stats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", account?.id, "orders"] });
      queryClient.invalidateQueries({ queryKey: ["/api/accounts", account?.id, "orders", "current"] });
    },
    onError: (error: any) => {
      // The session belongs to a different account than this page (nothing was written).
      // Keep the draft — it is saved under THIS account's id, so it comes back after
      // signing in as this account — and send the customer to sign in.
      if (error?.message?.includes('ACCOUNT_MISMATCH')) {
        flushCurrentCart();
        let signedInAs: string | undefined;
        try {
          signedInAs = JSON.parse(String(error.message).replace(/^\d+:\s*/, ''))?.signedInAccountName;
        } catch {}
        toast({
          title: "Signed in to a different account",
          description: signedInAs
            ? `You're signed in as ${signedInAs}. Sign in as ${account?.name ?? 'this account'} to order for them. Your cart is saved.`
            : `Sign in as ${account?.name ?? 'this account'} to order for them. Your cart is saved.`,
          variant: "destructive"
        });
        const loginPath = orgSlug ? `/org/${orgSlug}` : "/";
        setTimeout(async () => {
          await apiRequest("POST", "/api/customer/logout", {}).catch(() => {});
          window.location.href = loginPath;
        }, 2500);
        return;
      }
      // No valid session at submit time (expired or cleared since the page loaded) and
      // the server will not take the order on a phone number alone (OW-7). Nothing was
      // written. Keep the draft and land on the sign-in card — no toast, it explains
      // itself (same as the verify-session redirect above).
      if (error?.message?.includes('SESSION_REQUIRED')) {
        flushCurrentCart();
        setIsRedirecting(true);
        setLocation(orgSlug ? `/org/${orgSlug}` : "/");
        return;
      }
      // The day picked is not one the schedule offers any more — usually its cutoff
      // passed while the page was open. Only once the server's date check refuses (C3).
      // Nothing was written: keep the cart, refresh the schedule, ask for another day.
      if (error?.message?.includes('FULFILLMENT_DATE_UNAVAILABLE')) {
        queryClient.invalidateQueries({ queryKey: ["/api/order-window-config/resolved", account?.id] });
        toast({
          title: "That day isn't available any more",
          description: "Nothing was sent. Pick another day and send your order again.",
          variant: "destructive",
        });
        return;
      }
      // Something in the cart can no longer be ordered (OW-1). Nothing was written.
      // Say which, take it out of the cart, refresh the menu, and leave the rest of
      // the draft exactly as it was for the customer to check and send again.
      if (error?.message?.includes('PRODUCTS_UNAVAILABLE')) {
        let unavailable: { productIds?: number[]; names?: string[] } = {};
        try {
          unavailable = JSON.parse(String(error.message).replace(/^\d+:\s*/, ''))?.unavailable ?? {};
        } catch {}
        const gone: string[] = [...(unavailable.names ?? [])];
        cartTrail.unavailableRemoved(unavailable.productIds ?? [], activeDateRef.current);
        (unavailable.productIds ?? []).forEach((id) => {
          const name = seenProductNames.current.get(id);
          if (name) gone.push(name);
          form.setValue(`quantities.${productKey(id)}`, 0, { shouldDirty: true });
        });
        queryClient.invalidateQueries({ queryKey: ["/api/products/active"] });
        toast({
          title: gone.length === 1 ? "1 item is no longer available" : "Some items are no longer available",
          description: gone.length > 0
            ? `${gone.join(', ')} — removed from your cart. Nothing was sent. Check your order and send it again.`
            : "They were removed from your cart. Nothing was sent. Check your order and send it again.",
          variant: "destructive",
        });
        return;
      }
      // If session expired on a group account, redirect to login
      if (error?.message?.includes('SESSION_EXPIRED_GROUP_ACCOUNT')) {
        toast({
          title: "Session expired",
          description: "Please log in again to continue ordering.",
          variant: "destructive"
        });
        const loginPath = orgSlug ? `/org/${orgSlug}` : "/";
        setTimeout(() => { window.location.href = loginPath; }, 1500);
        return;
      }
      toast({
        title: "Error submitting order",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    },
  });

  // Handle delivery location submission for kiosk orders
  const handleDeliveryLocationSubmit = async () => {
    if (!lastOrderId) return;
    
    const savedLocation = deliveryLocation.trim();
    
    try {
      await apiRequest("PATCH", `/api/orders/${lastOrderId}`, {
        deliveryLocation: savedLocation || null
      });
      
      // Save the location for the confirmation message before clearing
      setConfirmedDeliveryLocation(savedLocation || null);
      
      // Close delivery dialog and show confirmation
      setShowDeliveryLocationDialog(false);
      setShowKioskConfirmation(true);
      setDeliveryLocation("");
      
      // Auto-close confirmation and redirect after 5 seconds
      setTimeout(async () => {
        setShowKioskConfirmation(false);
        setConfirmedDeliveryLocation(null);
        // Clear customer session and redirect to kiosk entry
        await apiRequest("POST", "/api/customer/logout", {}).catch(() => {});
        forgetCustomerDataOnLeave(orgSlug);
        const kioskPath = orgSlug ? `/org/${orgSlug}/kiosk` : "/";
        setLocation(kioskPath);
      }, 5000);
    } catch (error) {
      toast({
        title: "Error saving location",
        description: "Your order was placed, but we couldn't save your location. Staff will find you.",
        variant: "destructive"
      });
      setConfirmedDeliveryLocation(null);
      setShowDeliveryLocationDialog(false);
      setShowKioskConfirmation(true);

      // Still redirect after 5 seconds even on error
      setTimeout(async () => {
        setShowKioskConfirmation(false);
        await apiRequest("POST", "/api/customer/logout", {}).catch(() => {});
        forgetCustomerDataOnLeave(orgSlug);
        const kioskPath = orgSlug ? `/org/${orgSlug}/kiosk` : "/";
        setLocation(kioskPath);
      }, 5000);
    }
  };

  const handleSkipDeliveryLocation = () => {
    setConfirmedDeliveryLocation(null);
    setShowDeliveryLocationDialog(false);
    setShowKioskConfirmation(true);
    setDeliveryLocation("");
    
    // Auto-close confirmation and redirect after 5 seconds
    setTimeout(async () => {
      setShowKioskConfirmation(false);
      // Clear customer session and redirect to kiosk entry
      await apiRequest("POST", "/api/customer/logout", {}).catch(() => {});
      forgetCustomerDataOnLeave(orgSlug);
      const kioskPath = orgSlug ? `/org/${orgSlug}/kiosk` : "/";
      setLocation(kioskPath);
    }, 5000);
  };



  // Calculate order preview
  const quantities = form.watch("quantities");
  const notes = form.watch("notes");
  
  // Calculate existing order quantities and notes for comparison
  // Baseline = the BOUND order only (the one this cart edits), not a merge of every order.
  const existingQuantities = useMemo(() => {
    if (!boundOrder || !products.length) return {};

    const existing: Record<string, number> = {};
    products.forEach((product) => {
      existing[productKey(product.id)] = 0;
    });

    boundOrder.items.forEach(item => {
      const product = products.find(p => p.name === item.product.name);
      if (product) {
        const safeKey = productKey(product.id);
        existing[safeKey] = (existing[safeKey] || 0) + item.quantity;
      }
    });

    return existing;
  }, [boundOrder, products]);

  const orderItems = products
    .map((product) => ({
      product: product.name,
      productId: product.id,
      price: product.price,
      taxPercent: product.taxPercent,
      category: categories.find(c => c.id === product.categoryId)?.name || 'Other',
      quantity: quantities[productKey(product.id)] || 0,
      hasExistingOrder: (existingQuantities[productKey(product.id)] || 0) > 0
    }))
    .filter(item => item.quantity > 0 || item.hasExistingOrder); // Show items with quantity > 0 OR existing orders
  
  // Group order items by category for cart display
  const orderItemsByCategory = useMemo(() => {
    const grouped: Record<string, typeof orderItems> = {};
    orderItems.forEach(item => {
      const cat = item.category || 'Other';
      if (!grouped[cat]) grouped[cat] = [];
      grouped[cat].push(item);
    });
    return grouped;
  }, [orderItems]);

  const totalItems = orderItems.reduce((sum, item) => sum + item.quantity, 0);

  // ---- First-order guides (components/ordering-intro.tsx, components/first-order-tips.tsx) ------
  // For a buyer who has never placed an order themselves — staff-keyed orders don't count, they never
  // saw this screen — until each guide is done on this device. Never in a kiosk or on a demo org
  // (the demo has its own welcome).
  const [seenTips, setSeenTips] = useState<Set<FirstOrderTip> | null>(null);
  useEffect(() => {
    let live = true;
    getSeenFirstOrderTips().then((seen) => { if (live) setSeenTips(seen); });
    return () => { live = false; };
  }, []);
  const markTipsSeen = useCallback((tips: readonly FirstOrderTip[]) => {
    setSeenTips((prev) => {
      if (!prev || tips.every((t) => prev.has(t))) return prev;
      const next = new Set(prev);
      tips.forEach((t) => next.add(t));
      void setSeenFirstOrderTips(next);
      return next;
    });
  }, []);
  const dismissTip = useCallback((tip: FirstOrderTip) => markTipsSeen([tip]), [markTipsSeen]);

  // ---- Phase 2: omni-present order switcher ("Adding to" bar on the browsing screens) ------
  // Shows which dated order the customer's +/- taps fill and lets them hop between orders or start
  // a new day's order without opening the cart. Purely a new TRIGGER for the existing date-keyed
  // binding (bindToDate / changeDateKeepCart) — it cannot create a same-date duplicate.
  const orderBarMethod: 'delivery' | 'pickup' =
    (boundOrder?.fulfillmentType as 'delivery' | 'pickup') || fulfillmentType || 'delivery';
  const orderBarDate = boundOrder
    ? (boundOrder.fulfillmentDate || boundOrder.pickupDate || '')
    : activeFulfillmentDate;
  // The active slot reads as a real order once it has a submitted order OR any items in progress;
  // only a truly empty new slot reads as "New order / choose a day".
  const barIsExistingOrder = !!boundOrder || (!!orderBarDate && totalItems > 0);

  // One cart-panel title shared by quick-view and full-review: an existing order reads as "Order #id"
  // (Jon's preference), anything in progress reads as "New order" (with its day once chosen). Replaces
  // the old "Your Cart"/"Your Order"/"New order" grammar split so the title always names which order
  // you're in — which matters now that a customer can hold several at once.
  const cartTitle = boundOrder
    ? `Order #${boundOrder.id}`
    : activeFulfillmentDate
      ? `New order · ${formatChipDate(activeFulfillmentDate)}`
      : 'New order';

  // The switcher lists BOTH submitted orders and in-progress (unsubmitted) drafts that carry items,
  // so a half-built second order shows up as an order to hop back to — not as "pick a day". Built
  // fresh when the dropdown opens (drafts live in localStorage, which isn't reactive).
  const parseDraftDateKey = (dateKey: string): { type: 'delivery' | 'pickup'; date: string } | null => {
    const us = dateKey.indexOf('_');
    if (us < 0) return null;
    const type = dateKey.slice(0, us);
    const date = dateKey.slice(us + 1);
    if (type !== 'delivery' && type !== 'pickup') return null;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null; // skip the dateless `_new` slot
    return { type, date };
  };
  const countSavedItems = (data: any): number =>
    Object.values((data && data.quantities) || {}).reduce<number>((s, v) => s + (Number(v) > 0 ? Number(v) : 0), 0);
  const buildSwitcherOrders = (): SwitcherOrder[] => {
    const byDate = new Map<string, SwitcherOrder>();
    sortedOrders.forEach((o) => {
      const date = o.fulfillmentDate || o.pickupDate || '';
      if (date) byDate.set(date, { date, type: (o.fulfillmentType || 'delivery') as 'delivery' | 'pickup', totalItems: o.totalItems, isDraft: false, serverId: o.id });
    });
    if (account) {
      try {
        const prefix = getStorageKeyPrefix(account.id);
        const keys: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const k = localStorage.key(i);
          if (k && k.startsWith(prefix)) keys.push(k);
        }
        keys.forEach((k) => {
          const parsed = parseDraftDateKey(k.slice(prefix.length));
          if (!parsed || byDate.has(parsed.date)) return; // a submitted order on that date wins
          const data = loadCartFromStorage(account.id, k.slice(prefix.length)); // TTL + period validated
          const count = countSavedItems(data);
          if (count > 0) byDate.set(parsed.date, { date: parsed.date, type: parsed.type, totalItems: count, isDraft: true });
        });
      } catch { /* localStorage unavailable — fall back to submitted orders only */ }
    }
    // Overlay the order being composed right now (the form is fresher than its debounced draft).
    if (!boundOrder && activeFulfillmentDate && totalItems > 0) {
      byDate.set(activeFulfillmentDate, { date: activeFulfillmentDate, type: fulfillmentType, totalItems, isDraft: true });
    }
    return Array.from(byDate.values()).sort((a, b) => a.date.localeCompare(b.date));
  };
  // Start a brand-new day's order from the switcher, staying on the menu. If a new (uncommitted) cart
  // already has items, carry them onto the chosen day rather than re-seeding empty (which would
  // strand those items under the old day's draft key). Callers close the switcher sheet themselves.
  const selectNewDay = (dateStr: string, type: 'delivery' | 'pickup') => {
    if (!account || !dateStr) return;
    if (!boundOrder && activeFulfillmentDate === dateStr) return; // already composing this day
    if (!boundOrder && totalItems > 0) {
      clearCartFromStorage(account.id, activeDateRef.current); // drop the old-day draft so it can't orphan
      changeDateKeepCart(dateStr, type);
    } else {
      bindToDate(dateStr, type);
    }
  };
  const showOrderBar = !!account
    && (currentView === 'categories' || currentView === 'items' || currentView === 'search')
    && currentPeriodOrders.length >= 1;

  // Which first-order guide shows (shared/buyerFirstRun.ts). Never in a kiosk or on a demo org (the
  // demo has its own welcome), and never over the order switcher or the account menu.
  const guidesReady = !!account && !!seenTips && isInitialized && recentOrdersFetched
    && !organization?.isDemo && !isKioskMode();
  // Orders the buyer placed themselves: staff-keyed orders don't count, they never saw this screen.
  // The two lists overlap, so count distinct ids.
  const ownOrders = new Set(
    [...recentOrders, ...currentPeriodOrders].filter((o) => o.source === 'customer_app').map((o) => o.id),
  ).size;
  const firstTimer = ownOrders === 0;
  const activeTip = guidesReady && !switcherSheetOpen && !accountSwitcherOpen
    ? currentFirstOrderTip({
        view: currentView,
        totalItems,
        drawer: cartDrawerMode,
        ownOrders,
        // components/vendor-top-bar.tsx is lg:hidden.
        vendorSwitcherShown: typeof window !== 'undefined' && window.innerWidth < 1024,
        orderBarShown: showOrderBar,
        seen: seenTips!,
      })
    : null;
  const tipMethod = !deliveryEnabled && pickupEnabled ? 'pickup' : deliveryEnabled && pickupEnabled ? 'delivery or pickup' : 'delivery';
  // Doing the step ends its guide: adding an item ends the intro and the vendor callout, opening the
  // review ends "review", opening the order switcher ends "days".
  useEffect(() => {
    if (!guidesReady) return;
    if (totalItems > 0 && firstTimer) markTipsSeen(['intro', 'vendor']);
    if (cartDrawerMode === 'full-review' && firstTimer) markTipsSeen(['intro', 'vendor', 'review']);
    if (switcherSheetOpen) markTipsSeen(['days']);
  }, [guidesReady, firstTimer, totalItems, cartDrawerMode, switcherSheetOpen, markTipsSeen]);

  const openSwitcherSheet = () => {
    setAccountSwitcherOpen(false);
    setSwitcherOrders(buildSwitcherOrders());
    setSwitcherSheetOpen(true);
  };

  // Shared switcher body — the "Your orders" list + "new order for another day" chips. Rendered in
  // both the top dropdown and the bottom sheet so the two stay identical. `close` dismisses whichever
  // host opened it before the bind/seed runs.
  const renderSwitcherList = (close: () => void) => (
    <>
      <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
        <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Your orders</p>
      </div>
      <div className="py-1">
        {switcherOrders.map((o) => {
          const selected = o.serverId != null
            ? boundOrder?.id === o.serverId
            : (!boundOrder && activeFulfillmentDate === o.date);
          return (
            <button
              key={o.serverId != null ? `srv-${o.serverId}` : `draft-${o.date}`}
              type="button"
              role="menuitem"
              onClick={() => { close(); bindToDate(o.date, o.type); }}
              className={`w-full flex items-center justify-between gap-3 px-4 min-h-[44px] py-2 text-left transition-colors ${selected ? 'bg-[#D6E5DB]' : 'hover:bg-gray-50 active:bg-gray-100'}`}
              data-testid={`order-switch-${o.date}`}
            >
              <span className="flex items-center gap-2.5 min-w-0">
                {o.type === 'pickup'
                  ? <MapPin className={`h-[17px] w-[17px] flex-shrink-0 ${selected ? 'text-[#3A6247]' : 'text-gray-400'}`} />
                  : <Truck className={`h-[17px] w-[17px] flex-shrink-0 ${selected ? 'text-[#3A6247]' : 'text-gray-400'}`} />}
                <span className="min-w-0">
                  <span className={`block text-sm font-medium truncate ${selected ? 'text-[#3A6247]' : 'text-gray-900'}`}>
                    {formatChipDate(o.date)}
                  </span>
                  <span className={`block text-xs ${o.isDraft ? 'text-[#92400E] font-medium' : selected ? 'text-[#3A6247]' : 'text-gray-500'}`}>
                    {o.isDraft ? 'Not sent yet' : 'Sent'} · {o.totalItems} {o.totalItems === 1 ? 'item' : 'items'}
                  </span>
                </span>
              </span>
              {selected && <Check className="h-[17px] w-[17px] text-[#3A6247] flex-shrink-0" />}
            </button>
          );
        })}
      </div>
      {(() => {
        const takenDates = new Set(switcherOrders.map((s) => s.date));
        const newDays = windowConfig
          ? getAvailableFulfillmentDates(orderBarMethod, windowConfig).filter((d) => !takenDates.has(d.value))
          : [];
        return (
          <div className="border-t border-gray-200">
            <p className="px-4 pt-2 pb-1 text-[11px] font-medium text-gray-500 tracking-wide flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5 text-[#467B5A]" /> New order for another day
            </p>
            {newDays.length > 0 ? (
              <div className="px-3 pb-2.5 flex gap-2 overflow-x-auto">
                {/* With 3+ days each chip gets minWidth 40%, so ~2.5 fit — the half-visible chip
                    IS the scroll affordance (a full-width lone chip reads as "only one option"). */}
                {newDays.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    role="menuitem"
                    onClick={() => { close(); selectNewDay(d.value, orderBarMethod); }}
                    className="flex-shrink-0 min-h-[40px] rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[13px] font-medium text-gray-700 hover:border-gray-300 active:bg-gray-50 transition-colors"
                    style={newDays.length > 2 ? { minWidth: '40%' } : undefined}
                    data-testid={`new-order-day-${d.value}`}
                  >
                    {formatChipDate(d.value)}
                  </button>
                ))}
              </div>
            ) : (
              <p className="px-4 pb-3 text-xs text-gray-400">You have an order for every available {orderBarMethod} day.</p>
            )}
          </div>
        );
      })()}
    </>
  );

  // Close the cart. If the customer tapped "+ New order" but never committed to it (no day picked,
  // no items), don't strand them in a blank "new order" — revert to their existing orders.
  const closeCart = () => {
    setCartDrawerMode('closed');
    if (!boundOrder && !activeFulfillmentDate && totalItems === 0 && moveCancelOrderId == null && currentPeriodOrders.length > 0) {
      setIsInitialized(false); // restore effect re-binds to the earliest existing order
    }
  };

  // Existing notes from the BOUND order (the one this cart is editing)
  const existingNotes = useMemo(() => boundOrder?.notes || "", [boundOrder]);

  // Check if current quantities or notes differ from the bound order
  const hasChanges = useMemo(() => {
    // No bound order (composing a new dated order): any items or notes mean changes
    if (!boundOrder) {
      return totalItems > 0 || (notes && notes.trim().length > 0);
    }

    // Compare current quantities with the bound order's quantities
    const quantityChanged = products.some((product) => {
      const safeKey = productKey(product.id);
      const currentQty = quantities[safeKey] || 0;
      const existingQty = existingQuantities[safeKey] || 0;
      return currentQty !== existingQty;
    });

    // Compare current notes with existing notes
    const notesChanged = (notes || "").trim() !== (existingNotes || "").trim();

    // Method/date changes count too (e.g. delivery↔pickup on the same day). Comparing against the
    // bound order's own method/date means REVERTING back to the original clears the changed state,
    // so the submit button greys out again when there's no net change.
    const boundMethod = (boundOrder.fulfillmentType || 'delivery') as 'delivery' | 'pickup';
    const boundDate = boundOrder.fulfillmentDate || boundOrder.pickupDate || '';
    const methodChanged = fulfillmentType !== boundMethod;
    const dateChanged = activeFulfillmentDate !== boundDate;

    return quantityChanged || notesChanged || methodChanged || dateChanged;
  }, [quantities, existingQuantities, products, boundOrder, totalItems, notes, existingNotes, fulfillmentType, activeFulfillmentDate]);

  // Categories the org has hidden from customers. Filtering here (not just in the
  // category-tile view) means search/typeahead results can't surface hidden-category
  // products — that bypass was how CNP customers kept ordering Apple Cider after the
  // admin hid the category. The server now also filters /products/active, but a stale
  // cached product list would reintroduce the leak without this.
  const hiddenCategoryIds = useMemo(
    () => new Set(categories.filter(c => c.isVisibleToCustomers === false).map(c => c.id)),
    [categories]
  );

  // Filter products based on org category visibility + account's custom menu settings
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      if (product.categoryId != null && hiddenCategoryIds.has(product.categoryId)) {
        return false;
      }
      if (!account?.customMenu) {
        return true; // No custom menu — org-level visibility is the only filter
      }
      // Check if product is enabled in custom menu
      // Default to visible (true) unless explicitly set to false
      const productKey = product.id.toString();
      const categoryKey = product.categoryId.toString();
      const productEnabled = account.customMenu?.products?.[productKey] !== false;
      // Check if category is enabled in custom menu
      const categoryEnabled = account.customMenu?.categories?.[categoryKey] !== false;

      return productEnabled && categoryEnabled;
    });
  }, [products, account?.customMenu, hiddenCategoryIds]);

  // Group filtered products by category
  const productsByCategory = filteredProducts.reduce((acc, product) => {
    const categoryId = product.categoryId || 1;
    if (!acc[categoryId]) {
      acc[categoryId] = [];
    }
    acc[categoryId].push(product);
    return acc;
  }, {} as Record<number, Product[]>);

  // Navigation handlers
  const navigateToCategory = (categoryId: number) => {
    setSelectedCategoryId(categoryId);
    setCurrentView('items');
    // The document is the scroller and views swap in place, so a scroll position from a long
    // category list carries into the next view — a short category then renders below the fold
    // and looks empty. Every view switch starts at the top.
    window.scrollTo(0, 0);
  };

  const navigateBack = () => {
    window.scrollTo(0, 0);
    if (currentView === 'items') {
      setCurrentView('categories');
      setSelectedCategoryId(null);
      setSearchQuery('');
      setUserTyped('');
    } else if (currentView === 'search') {
      setCurrentView('categories');
      setSelectedCategoryId(null);
      setSearchQuery('');
      setUserTyped('');
    } else if (currentView === 'market-report') {
      setCurrentView('categories');
    }
  };

  // Browser back, Safari's edge swipe and Android's back button return from a sub-view to
  // the categories, like the arrow (lib/menuBackEntry.ts).
  const navigateBackRef = useRef(navigateBack);
  navigateBackRef.current = navigateBack;
  const menuBackEntry = useRef<MenuBackEntry | null>(null);
  useLayoutEffect(() => {
    const entry = createMenuBackEntry(window, () => navigateBackRef.current());
    menuBackEntry.current = entry;
    return () => {
      entry.dispose();
      menuBackEntry.current = null;
    };
  }, []);
  const inMenuSubview = currentView === 'items' || currentView === 'search' || currentView === 'market-report';
  // A layout effect, so the entry is pushed before the sub-view paints: the iOS swipe
  // shows a snapshot of the page as it was when its entry was left — the categories.
  useLayoutEffect(() => {
    menuBackEntry.current?.sync(inMenuSubview);
    setNativeBackSwipe(inMenuSubview);
  }, [inMenuSubview]);
  useEffect(() => () => setNativeBackSwipe(false), []);

  const toggleCartDetails = () => {
    if (cartDrawerMode !== 'closed') {
      setCartDrawerMode('closed');
    } else {
      setCartDrawerMode('quick-view');
    }
  };

  const handleReviewOrder = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCartDrawerMode('full-review');
  };

  // Quantity editing handlers
  const handleQuantityClick = (currentValue: number, productId: number) => {
    setEditingQuantity(productId);
    setEditQuantityValue(currentValue.toString());
  };

  const handleQuantityInputSave = (field: any) => {
    const newValue = parseInt(editQuantityValue) || 0;
    const clampedValue = Math.max(0, newValue);
    field.onChange(clampedValue);
    setEditingQuantity(null);
    setEditQuantityValue('');
  };

  const handleQuantityInputKeyDown = (e: React.KeyboardEvent, field: any) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleQuantityInputSave(field);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingQuantity(null);
      setEditQuantityValue('');
    }
  };

  // Filter products based on search query (use userTyped for matching).
  // Alias-aware: "romas" finds "Roma Tomatoes 25lb" when the admin has set it up.
  const searchResults = useMemo(() => {
    const query = userTyped.trim();
    if (!query) return [];
    const q = query.toLowerCase();
    return filteredProducts.filter(product =>
      product.name.toLowerCase().includes(q) ||
      aliasMatchesSearch((product as { aliases?: unknown }).aliases, q)
    );
  }, [filteredProducts, userTyped]);

  // Get products for current category
  const categoryProducts = useMemo(() => {
    if (currentView !== 'items' || !selectedCategoryId) return [];
    if (userTyped.trim()) {
      return searchResults;
    }
    return filteredProducts.filter(p => p.categoryId === selectedCategoryId);
  }, [filteredProducts, currentView, selectedCategoryId, userTyped, searchResults]);

  // Each list as the menu draws it: an item's units share one card. A card is headed by
  // its item, each unit a tag and a price (shared/productNames.ts); the whole name is for
  // the order summary, pick sheets and invoices.
  const categoryGroups = useMemo(() => groupProductSizes(categoryProducts), [categoryProducts]);
  const categoryCardLabels = useMemo(() => menuCardLabels(categoryGroups), [categoryGroups]);
  const searchGroups = useMemo(() => groupProductSizes(searchResults), [searchResults]);
  const searchCardLabels = useMemo(() => menuCardLabels(searchGroups), [searchGroups]);

  // Typeahead autocomplete handler
  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>, inputRef: React.RefObject<HTMLInputElement>) => {
    const value = e.target.value;
    const typed = isDeleting.current ? value : value.substring(0, e.target.selectionStart ?? value.length);
    
    setUserTyped(typed);
    
    if (isDeleting.current || !typed.trim()) {
      setSearchQuery(typed);
      isDeleting.current = false;
      return;
    }

    const typedLower = typed.toLowerCase();
    const match = filteredProducts.find(p => p.name.toLowerCase().startsWith(typedLower));

    if (match) {
      const completed = typed + match.name.substring(typed.length);
      setSearchQuery(completed);
      requestAnimationFrame(() => {
        const el = inputRef.current;
        if (el) {
          el.value = completed;
          el.setSelectionRange(typed.length, completed.length);
        }
      });
    } else {
      setSearchQuery(typed);
    }
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      isDeleting.current = true;
    } else {
      isDeleting.current = false;
    }
    if (e.key === 'Tab' && searchQuery && userTyped && searchQuery !== userTyped) {
      e.preventDefault();
      setUserTyped(searchQuery);
      const input = e.currentTarget;
      input.setSelectionRange(searchQuery.length, searchQuery.length);
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const query = userTyped.trim() || searchQuery.trim();
      if (query) {
        setUserTyped(query);
        setSearchQuery(query);
        setCurrentView('search');
        setSelectedCategoryId(null);
      }
    }
  };

  // Update search functionality to also handle Enter key search
  const handleSearch = () => {
    const query = userTyped.trim() || searchQuery.trim();
    if (query) {
      setUserTyped(query);
      setSearchQuery(query);
      setCurrentView('search');
      setSelectedCategoryId(null);
      window.scrollTo(0, 0);
    }
  };

  // Handle print order functionality - navigate to order-summary page
  // (avoids window.open dead-end on iOS home screen shortcuts / standalone PWA)
  // One-tap print from the cart's printer icon (founder ask, 2026-09-04): go straight to
  // the print dialog instead of parking on the summary page and asking for a second tap.
  //
  // Native: WKWebView cannot print, so the tap hands a SIGNED summary URL (+print=1) to
  // Safari via window.open — see order-summary.tsx for why it must be synchronous and
  // therefore prefetched here whenever a bound order is being reviewed.
  // Web: navigate to the summary with ?print=1, which raises the dialog on render.
  const [nativePrintUrl, setNativePrintUrl] = useState<{ orderId: number; url: string } | null>(null);
  useEffect(() => {
    if (!isNativeApp() || cartDrawerMode !== 'full-review' || !boundOrder?.id) return;
    if (nativePrintUrl?.orderId === boundOrder.id) return;
    let cancelled = false;
    fetch(getApiPath(`/api/customer/order/${boundOrder.id}/print-url`), { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (!cancelled && j?.printUrl) setNativePrintUrl({ orderId: boundOrder.id, url: j.printUrl }); })
      .catch(() => { /* falls back to the in-app summary page */ });
    return () => { cancelled = true; };
  }, [cartDrawerMode, boundOrder?.id, nativePrintUrl?.orderId]);

  const handlePrintOrder = () => {
    let orderId: number | undefined;
    if (boundOrder) {
      // Print the order this cart is currently bound to, not an arbitrary [0].
      orderId = boundOrder.id;
    } else {
      // Fall back to account order history cache
      const accountOrdersData = queryClient.getQueryData(["/api/accounts", account?.id, "orders", orgSlug]) as any[] || [];
      if (accountOrdersData.length > 0) {
        const mostRecentOrder = accountOrdersData.reduce((latest: any, current: any) =>
          current.id > latest.id ? current : latest
        );
        orderId = mostRecentOrder.id;
      }
    }

    if (!orderId || !orgSlug || !account?.id) return;
    if (isNativeApp() && hasNativePrinter()) {
      // In-app iOS print sheet (PrinterPlugin) — no Safari hop.
      printPdfNatively(getApiPath(`/api/customer/order/${orderId}/pdf`), `Order ORD-${String(orderId).padStart(3, "0")}`)
        .catch(() => toast({ title: "Couldn't print", description: "Please try again.", variant: "destructive" }));
      return;
    }
    if (isNativeApp() && nativePrintUrl?.orderId === orderId) {
      window.open(nativePrintUrl.url, "_blank"); // synchronous: inside the tap's activation
      return;
    }
    setLocation(`/org/${orgSlug}/order-summary/${account.id}/${orderId}?print=1`);
  };

  // Get category items count and total quantity
  const getCategoryStats = (categoryId: number) => {
    const categoryProductsForCategory = productsByCategory[categoryId] || [];
    const totalQuantity = categoryProductsForCategory.reduce((sum, product) => {
      const safeKey = productKey(product.id);
      return sum + (quantities[safeKey] || 0);
    }, 0);
    return { itemCount: categoryProductsForCategory.length, totalQuantity };
  };

  const [deadlineTimeLeft, setDeadlineTimeLeft] = useState<string>('');
  const [deadlineDayName, setDeadlineDayName] = useState<string>('');

  useEffect(() => {
    if (!fulfillmentDate || !windowConfig) return;
    const updateCountdown = () => {
      const result = getCountdownForDate(fulfillmentDate, fulfillmentType, windowConfig);
      setDeadlineTimeLeft(result.timeLeft);
      setDeadlineDayName(result.dayName);
    };
    updateCountdown();
    const interval = setInterval(updateCountdown, 60000);
    return () => clearInterval(interval);
  }, [fulfillmentDate, fulfillmentType, windowConfig]);



  const onSubmit = (data: CustomerOrderInput) => {
    // Check if ordering is currently allowed
    if (!isOrderingAllowed()) {
      toast({
        title: "Ordering Closed",
        description: getOrderingClosedMessage(),
        variant: "destructive",
      });
      return;
    }
    
    if (!account) {
      toast({
        title: "Account not found",
        description: "Unable to identify your account. Please contact support.",
        variant: "destructive",
      });
      return;
    }

    const needsFulfillmentDate = (deliveryEnabled || pickupEnabled);
    if (needsFulfillmentDate && !fulfillmentDate && !pickupDate) {
      toast({
        title: "Fulfillment date required",
        description: fulfillmentType === "pickup" ? "Please select a pickup day before submitting." : "Please select a delivery date before submitting.",
        variant: "destructive",
      });
      return;
    }

    // Block an empty submit for a date with no order to edit (a brand-new dated order with no
    // items). Editing an existing bound order down to zero is still allowed (editingOrderId set).
    // A 0-item order is never submittable — removing an order is done via "Cancel order", not by
    // zeroing the cart. Applies to new orders and to an existing order edited down to empty.
    if (totalItems === 0) {
      toast({
        title: "No items selected",
        description: "Please select at least one product with a quantity greater than 0.",
        variant: "destructive",
      });
      return;
    }

    submitOrderMutation.mutate(data);
  };

  // Show loading while verifying session
  if (sessionChecking) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <img src={freshOrderLogo} alt="FreshOrder" className="h-16 mx-auto mb-6" />
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Block rendering if redirecting to login
  if (isRedirecting) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <img src={freshOrderLogo} alt="FreshOrder" className="h-16 mx-auto mb-6" />
          <p className="text-gray-600">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  // Demo org: full-screen transition while navigating to admin view
  if (demoTransitioning) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center">
          <img src={freshOrderLogo} alt="FreshOrder" className="h-16 mx-auto mb-6" />
          <p className="text-xl font-semibold text-gray-900 mb-2">Order submitted!</p>
          <p className="text-gray-600 mb-6">Preparing your admin view...</p>
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto" />
        </div>
      </div>
    );
  }

  // Redirect if session not verified (handled by useEffect above)
  if (!sessionVerified) {
    return null;
  }

  if (productsLoading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="text-center">Loading products...</div>
      </div>
    );
  }

  // Show 404 error if custom URL doesn't match any account
  if (isCustomUrlRoute && !accountLoading && !fetchedAccount) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <img src={freshOrderLogo} alt="FreshOrder" className="h-16 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Page Not Found</h1>
          <p className="text-gray-600 mb-6">
            The ordering page you're looking for doesn't exist or has been removed.
          </p>
          <p className="text-gray-600">
            Please check the URL or contact your supplier for the correct link.
          </p>
        </div>
      </div>
    );
  }

  // Show inactive account message if account exists but is inactive
  if (isCustomUrlRoute && fetchedAccount && !fetchedAccount.isActive) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <img src={freshOrderLogo} alt="FreshOrder" className="h-16 mx-auto mb-6" />
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Ordering Page Inactive</h1>
          <p className="text-gray-600 mb-6">
            This ordering page is currently inactive.
          </p>
          <p className="text-gray-600">
            Please contact your supplier for assistance.
          </p>
        </div>
      </div>
    );
  }

  // The order list could not be read for this page: no cart until it can be (a cart seeded
  // from a stale list replaces the real order on submit — lib/cartSeed.ts).
  if (account && !isInitialized && ordersReadFailed({ isFetching: ordersFetching, isError: ordersError, dataUpdatedAt: ordersUpdatedAt }, pageMountedAt)) {
    return (
      <div className="min-h-screen min-h-[100dvh] bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center max-w-md" data-testid="orders-load-failed">
          <img src={freshOrderLogo} alt="FreshOrder" className="h-16 mx-auto mb-6" />
          <h1 className="text-xl font-semibold text-gray-900 mb-2">We couldn't load your orders</h1>
          <p className="text-gray-600 mb-6">Check your connection, then try again. Nothing in your order has changed.</p>
          <Button onClick={() => refetchOrders()} data-testid="button-retry-orders">Try again</Button>
        </div>
      </div>
    );
  }

  // Main mobile UI render with modern Voila-style design
  return (
    <div className="min-h-screen min-h-[100dvh] bg-customer-cream">
      {/* Capacitor shell only (no-op on web): vendor memory, push binding,
          offline order-guide snapshot, biometric enrollment offer. */}
      <NativeShellIntegration
        orgSlug={orgSlug}
        accountId={account?.id}
        orgName={orgContext?.organization?.name}
        logoUrl={orgContext?.organization?.logoUrl}
        faviconUrl={orgContext?.organization?.faviconUrl}
        customUrl={customUrl}
        sessionVerified={sessionVerified}
        isDemo={orgContext?.organization?.isDemo}
        products={products}
      />
      <OrderingIntro
        open={activeTip === 'intro'}
        vendorName={organization?.name || 'your supplier'}
        method={tipMethod}
        onDone={() => dismissTip('intro')}
      />
      <FirstOrderTips
        tip={activeTip === 'intro' ? null : activeTip}
        context={{
          method: cartDrawerMode === 'full-review' ? fulfillmentType : tipMethod,
          hasDeadline: showDeadlineMessages && !!fulfillmentDate && !!deadlineTimeLeft,
        }}
        onDismiss={dismissTip}
      />
      {/* Demo welcome lightbox — shown once per session for demo orgs only */}
      {showDemoWelcome && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={() => {
            sessionStorage.setItem('demo_welcome_seen', '1');
            setShowDemoWelcome(false);
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full mx-6 p-8 text-center">
            <img src={freshOrderLogo} alt="FreshOrder" className="h-14 mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Welcome!</h2>
            <p className="text-gray-600 mb-6">
              This is the portal your clients would use to submit orders. Give it a try!
            </p>
            <p className="text-sm text-gray-400">Tap anywhere to begin</p>
          </div>
        </div>
      )}
      <Form {...form}>
        <form id="order-form" onSubmit={form.handleSubmit(onSubmit, (errors) => console.error('🚫 RHF validation failed:', errors))}>
          
          {/* Header Section */}
          {/* sticky top-0 puts this under the status bar / Dynamic Island in the
              native shell (full-bleed webview), which made the back and search
              controls untappable. The inset resolves to 0 everywhere else. */}
          <div
            ref={stickyHeaderRef}
            className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50"
            style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
          >
            {/* Vendor identity bar (mobile): the vendor's logo + name anchor every customer
                page; with 2+ remembered vendors on this device it doubles as the switcher. */}
            <VendorTopBar
              orgSlug={orgSlug}
              orgName={organization?.name}
              logoUrl={organization?.logoUrl}
              faviconUrl={organization?.faviconUrl}
            />
            {/* Organization Logo - hidden on small screens to prevent interference with search bar */}
            <div className="absolute top-1 left-4 z-60 hidden lg:block">
              <div 
                className="block"
                data-testid="link-org-logo"
              >
                <img 
                  src={orgContext?.organization?.logoUrl || defaultProductLogo} 
                  alt={orgContext?.organization?.name ? `${orgContext.organization.name} logo` : "FreshOrder"} 
                  className="h-12 w-12 object-contain mt-[4px] mb-[4px]"
                  data-testid="logo-org"
                />
              </div>
            </div>
            
            <div className="px-3 py-3 sm:px-4">
              <div className="max-w-2xl mx-auto">
                {/* Back Button and Search Bar Layout */}
                <div className="flex items-center gap-3 w-full max-w-full overflow-hidden">
                  {(currentView === 'items' || currentView === 'search' || currentView === 'market-report') && (
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={navigateBack}
                      className="h-10 w-10 p-0 bg-gray-100 text-cn-brand-blue hover:bg-blue-100 hover:text-cn-brand-blue rounded-md flex-shrink-0"
                      data-testid="button-back"
                    >
                      <ArrowLeft className="h-6 w-6" />
                    </Button>
                  )}
                  
                  {/* Search Bar - always visible */}
                  <div className="relative flex-1 min-w-0 max-w-full">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-cn-brand-blue" />
                    <Input
                      ref={searchInputRef}
                      placeholder="Search produce..."
                      className="pl-10 bg-gray-100 text-cn-brand-blue border-none rounded-md w-full h-10 focus:outline-none focus:ring-0 focus:border-transparent focus:!ring-transparent focus:!border-gray-100 placeholder:text-cn-brand-blue"
                      data-testid="input-search"
                      autoComplete="off"
                      value={searchQuery}
                      onChange={(e) => handleSearchInput(e, searchInputRef)}
                      onKeyDown={handleSearchKeyDown}
                    />
                  </div>

                  {/* Logout button - only on categories home view */}
                  {currentView === 'categories' && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={async () => {
                        // Unbind push first — unregister needs the session logout destroys.
                        if (orgSlug) await unbindPushTokenFromVendor(orgSlug);
                        try {
                          await apiRequest("POST", "/api/customer/logout", {});
                        } catch (e) {
                          // Ignore logout errors
                        }
                        const loginPath = orgSlug ? `/org/${orgSlug}` : "/";
                        window.location.href = loginPath;
                      }}
                      className="h-10 w-10 p-0 text-gray-400 hover:text-gray-600 flex-shrink-0"
                      data-testid="button-customer-logout"
                    >
                      <LogOut className="h-5 w-5" />
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Order switcher lives in the BOTTOM cart bar now (thumb-reachable), not here at the
                top. Opened via openSwitcherSheet; rendered as the bottom sheet near the cart drawer. */}
          </div>

          {/* Main Content Area */}
          <div className={`bg-customer-cream ${totalItems > 0 ? 'pb-28' : 'pb-20'}`}>
            <AnimatePresence mode="popLayout">
              
              {/* Categories View */}
              {currentView === 'categories' && (
                <motion.div
                  key="categories"
                  initial={{ x: "-100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "-100%" }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className={`mobile-container bg-customer-cream ${totalItems > 0 ? 'pb-8' : ''} relative min-h-screen min-h-[100dvh]`}
                >
                  <div className="relative z-10 w-full">
                    <div className="max-w-2xl mx-auto pt-4">
                      {/* Welcome Message */}
                      {account && (
                        <div className="bg-white rounded-lg px-4 py-3 mobile-padding shadow-sm mb-4">
                          <div className="mb-2">
                            {hasGroupSiblings ? (
                              <div className="relative">
                                <button
                                  type="button"
                                  onClick={() => setAccountSwitcherOpen(!accountSwitcherOpen)}
                                  className="w-full flex items-center justify-between text-left"
                                >
                                  <h1 className="text-lg font-medium text-gray-600">
                                    Welcome, <span className="font-bold text-cn-brand-blue">{account.name}!</span>
                                  </h1>
                                  <div className="flex items-center gap-1 text-gray-400 ml-2 shrink-0">
                                    <Users className="h-4 w-4" />
                                    <ChevronDown className={`h-4 w-4 transition-transform ${accountSwitcherOpen ? 'rotate-180' : ''}`} />
                                  </div>
                                </button>
                                <AnimatePresence>
                                  {accountSwitcherOpen && (
                                    <>
                                      <div
                                        className="fixed inset-0 z-40"
                                        onClick={() => setAccountSwitcherOpen(false)}
                                      />
                                      <motion.div
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -8 }}
                                        transition={{ duration: 0.15 }}
                                        className="absolute left-0 right-0 top-full mt-1 z-50 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
                                      >
                                        <div className="px-3 py-2 bg-gray-50 border-b border-gray-100">
                                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Switch Account</p>
                                        </div>
                                        <div className="py-1">
                                          {siblingAccounts.map(sibling => (
                                            <button
                                              key={sibling.id}
                                              type="button"
                                              onClick={() => {
                                                if (!sibling.isCurrent) {
                                                  handleSwitchAccount(sibling.id, sibling.customUrl);
                                                } else {
                                                  setAccountSwitcherOpen(false);
                                                }
                                              }}
                                              className={`w-full flex items-center justify-between px-4 py-3 text-left transition-colors active:bg-gray-100 ${
                                                sibling.isCurrent
                                                  ? 'bg-blue-50'
                                                  : 'hover:bg-gray-50'
                                              }`}
                                            >
                                              <span className={`text-sm font-medium ${sibling.isCurrent ? 'text-cn-brand-blue' : 'text-gray-700'}`}>
                                                {sibling.name}
                                              </span>
                                              {sibling.isCurrent && (
                                                <Check className="h-4 w-4 text-cn-brand-blue shrink-0" />
                                              )}
                                            </button>
                                          ))}
                                        </div>
                                      </motion.div>
                                    </>
                                  )}
                                </AnimatePresence>
                              </div>
                            ) : (
                              <h1 className="text-lg font-medium text-gray-600">
                                Welcome, <span className="font-bold text-cn-brand-blue">{account.name}!</span>
                              </h1>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-3">
                    {/* Market Report — optional notice styled like the first category */}
                    {marketReport?.enabled && marketReport?.body && (
                      <div
                        onClick={() => setCurrentView('market-report')}
                        className="bg-white rounded-lg border border-gray-200 p-3 mobile-padding hover:shadow-md transition-all cursor-pointer active:scale-[0.98]"
                        data-testid="card-market-report"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-blue-50 text-cn-brand-blue flex-shrink-0">
                              <TrendingUp className="h-5 w-5" />
                            </div>
                            <div className="min-w-0">
                              <div className="font-semibold text-gray-900 truncate">
                                {marketReport.title || 'Market Report'}
                              </div>
                              {marketReport.contentUpdatedAt && (
                                <div className="text-xs text-gray-500">
                                  Updated {new Date(marketReport.contentUpdatedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                                </div>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
                        </div>
                      </div>
                    )}
                    {categories
                      .filter((category) => category.isVisibleToCustomers !== false && productsByCategory[category.id]?.length > 0)
                      .map((category) => {
                        const catId = category.id;
                        const categoryProducts = productsByCategory[catId] || [];
                        const { itemCount, totalQuantity } = getCategoryStats(catId);
                        
                        return (
                          <div
                            key={catId}
                            onClick={() => navigateToCategory(catId)}
                            className="bg-white rounded-lg border border-gray-200 p-3 mobile-padding hover:shadow-md transition-all cursor-pointer active:scale-[0.98]"
                            data-testid={`category-${catId}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <CategoryIcon icon={category.icon} size="md" />
                                <div className="font-semibold text-gray-900">
                                  {category.name}
                                </div>
                                {totalQuantity > 0 && (
                                  <Badge className="bg-cn-brand-green text-white rounded-md px-2 py-1 text-xs">
                                    {totalQuantity}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-sm text-gray-500">({itemCount})</span>
                                <ChevronRight className="h-5 w-5 text-gray-400" />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Items View */}
              {currentView === 'items' && (selectedCategoryId || userTyped.trim()) && (
                <motion.div
                  key="items"
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="mobile-container bg-customer-cream relative min-h-screen min-h-[100dvh]"
                >
                  <div className="max-w-2xl mx-auto space-y-4 relative z-10 w-full">
                    {categoryGroups.map(({ head, members }, index) => {
                      // Linked sizes (case / half case / each of one item) share one card.
                      if (members.length > 1) {
                        return (
                          <SizeGroupCard
                            key={`sizes-${head.id}`}
                            head={head}
                            members={members}
                            labels={categoryCardLabels[index]}
                            imageSrc={getProductImage(head.name, head, organization?.faviconUrl)}
                            control={form.control}
                            quantities={quantities}
                            editingQuantity={editingQuantity}
                            editQuantityValue={editQuantityValue}
                            onEditQuantityValue={setEditQuantityValue}
                            onQuantityClick={handleQuantityClick}
                            onQuantitySave={handleQuantityInputSave}
                            onQuantityKeyDown={handleQuantityInputKeyDown}
                          />
                        );
                      }
                      const product = members[0];
                      const safeKey = productKey(product.id);
                      const currentQuantity = quantities[safeKey] || 0;
                      
                      return (
                        <FormField
                          key={product.id}
                          control={form.control}
                          name={`quantities.${safeKey}`}
                          render={({ field }) => (
                            <div className="bg-white rounded-lg border border-gray-200 p-4 mobile-padding relative min-h-[120px] sm:min-h-[140px]">
                              <div className="flex gap-6">
                                {/* Product Image - much bigger size, left aligned */}
                                <div className="flex-shrink-0">
                                  <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                                    <img
                                      src={getProductImage(product.name, product, organization?.faviconUrl)}
                                      alt={product.name}
                                      className="w-full h-full object-cover rounded-lg"
                                      loading="lazy"
                                      decoding="async"
                                    />
                                  </div>
                                </div>
                                
                                {/* Product Info and Actions - stacked vertically like Voila */}
                                <div className="flex-1 flex flex-col justify-between min-h-[96px] sm:min-h-[112px]">
                                  <div className="mb-2">
                                    <div className="flex items-start justify-between">
                                      <h3 className="font-medium text-gray-900 text-sm sm:text-base mb-0.5">{categoryCardLabels[index].title}<UnitPill unit={categoryCardLabels[index].tags[0]} /></h3>
                                      {product.price && (
                                        <span className="text-sm font-medium text-gray-700 ml-2">${product.price}</span>
                                      )}
                                    </div>
                                    {product.subheading && (
                                      <p className="text-xs sm:text-sm text-gray-600 font-light">{product.subheading}</p>
                                    )}
                                  </div>
                                  
                                  {/* Add/Quantity Controls - full width buttons */}
                                  <div className="flex items-center">
                                    {currentQuantity === 0 ? (
                                      prevQuantities[product.id] !== undefined ? (
                                        <div className="flex items-center gap-2 w-full">
                                          <Button
                                            type="button"
                                            onClick={() => { field.onChange(1); }}
                                            className="bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white rounded-lg py-3 px-4 flex-1 h-11 font-medium"
                                            data-testid={`button-add-${product.id}`}
                                          >
                                            Add
                                          </Button>
                                          {/* Prev Qty mirrors Review Order: bg-blue-100/brand-blue is recall, brand-green is commit. */}
                                          <Button
                                            type="button"
                                            onClick={() => { field.onChange(prevQuantities[product.id]); }}
                                            className="bg-blue-100 text-cn-brand-blue hover:bg-cn-brand-blue hover:text-white rounded-lg py-3 px-4 flex-1 h-11 font-medium"
                                            data-testid={`button-prev-qty-${product.id}`}
                                          >
                                            {String(prevQuantities[product.id]).length >= 3 ? 'Prev Qty' : `Prev Qty: ${prevQuantities[product.id]}`}
                                          </Button>
                                        </div>
                                      ) : (
                                        <Button
                                          type="button"
                                          onClick={() => { field.onChange(1); }}
                                          className="bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white rounded-lg py-3 px-6 font-medium text-base h-11 w-full"
                                          data-testid={`button-add-${product.id}`}
                                        >
                                          Add
                                        </Button>
                                      )
                                    ) : (
                                      <div className="flex items-center gap-2 w-full">
                                        <Button
                                          type="button"
                                          onClick={() => { field.onChange(Math.max(0, currentQuantity - 1)); }}
                                          className="bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white rounded-lg py-3 px-4 flex-1 h-11 font-medium"
                                          data-testid={`button-minus-${product.id}`}
                                        >
                                          <Minus className="h-4 w-4" />
                                        </Button>
                                        {editingQuantity === product.id ? (
                                          <Input
                                            type="number"
                                            value={editQuantityValue}
                                            onChange={(e) => setEditQuantityValue(e.target.value)}
                                            onBlur={() => handleQuantityInputSave(field)}
                                            onKeyDown={(e) => handleQuantityInputKeyDown(e, field)}
                                            className="font-medium text-lg text-center h-11 px-3 flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                                            autoFocus
                                            data-testid={`input-quantity-${product.id}`}
                                          />
                                        ) : (
                                          <div className="flex-1 flex items-center justify-center h-11 bg-gray-50 rounded-lg mx-2 cursor-pointer hover:bg-gray-100 transition-colors"
                                            onClick={() => handleQuantityClick(currentQuantity, product.id)}
                                            data-testid={`text-quantity-${product.id}`}
                                          >
                                            <span className="font-medium text-lg">{currentQuantity}</span>
                                          </div>
                                        )}
                                        <Button
                                          type="button"
                                          onClick={() => { field.onChange(currentQuantity + 1); }}
                                          className="bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white rounded-lg py-3 px-4 flex-1 h-11 font-medium"
                                          data-testid={`button-plus-${product.id}`}
                                        >
                                          <Plus className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        />
                      );
                    })}
                  </div>
                </motion.div>
              )}

              {/* Market Report Detail View */}
              {currentView === 'market-report' && (
                <motion.div
                  key="market-report"
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="mobile-container bg-customer-cream relative min-h-screen min-h-[100dvh]"
                >
                  <div className="max-w-2xl mx-auto pt-4 relative z-10 w-full">
                    <div className="bg-white rounded-lg border border-gray-200 p-4 mobile-padding shadow-sm">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-50 text-cn-brand-blue flex-shrink-0">
                          <TrendingUp className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <h1 className="text-lg font-bold text-gray-900 truncate">
                            {marketReport?.title || 'Market Report'}
                          </h1>
                          {marketReport?.contentUpdatedAt && (
                            <div className="text-xs text-gray-500">
                              Last updated {new Date(marketReport.contentUpdatedAt).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-gray-800 whitespace-pre-wrap leading-relaxed">
                        {marketReport?.body}
                      </div>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Search Results View */}
              {currentView === 'search' && (
                <motion.div
                  key="search"
                  initial={{ x: "100%" }}
                  animate={{ x: 0 }}
                  exit={{ x: "100%" }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                  className="px-4 py-4 bg-customer-cream min-h-screen min-h-[100dvh]"
                >
                  <div className="max-w-2xl mx-auto">
                    <div className="mb-4">
                      <h2 className="text-lg font-semibold text-[#4b5563]">
                        Search Results for "{userTyped}"
                      </h2>
                      <p className="text-sm text-gray-600 mt-1">
                        {searchResults.length} products found
                      </p>
                    </div>
                    
                    {searchResults.length === 0 ? (
                      <div className="text-center py-8">
                        <p className="text-gray-500">No products found matching your search.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                      {searchGroups.map(({ head, members }, index) => {
                        // Linked sizes (case / half case / each of one item) share one card.
                        if (members.length > 1) {
                          return (
                            <SizeGroupCard
                              key={`sizes-${head.id}`}
                              head={head}
                              members={members}
                              labels={searchCardLabels[index]}
                              imageSrc={getProductImage(head.name, head, organization?.faviconUrl)}
                              control={form.control}
                              quantities={quantities}
                              editingQuantity={editingQuantity}
                              editQuantityValue={editQuantityValue}
                              onEditQuantityValue={setEditQuantityValue}
                              onQuantityClick={handleQuantityClick}
                              onQuantitySave={handleQuantityInputSave}
                              onQuantityKeyDown={handleQuantityInputKeyDown}
                            />
                          );
                        }
                        const product = members[0];
                        const safeKey = productKey(product.id);
                        const currentQuantity = quantities[safeKey] || 0;
                        
                        return (
                          <FormField
                            key={product.id}
                            control={form.control}
                            name={`quantities.${safeKey}`}
                            render={({ field }) => (
                              <div className="bg-white rounded-lg border border-gray-200 p-4 mobile-padding relative min-h-[120px] sm:min-h-[140px]">
                                <div className="flex gap-6">
                                  {/* Product Image - much bigger size, left aligned */}
                                  <div className="flex-shrink-0">
                                    <div className="w-24 h-24 sm:w-28 sm:h-28 bg-gray-100 rounded-lg flex items-center justify-center overflow-hidden">
                                      <img
                                        src={getProductImage(product.name, product, organization?.faviconUrl)}
                                        alt={product.name}
                                        className="w-full h-full object-cover rounded-lg"
                                        loading="lazy"
                                        decoding="async"
                                      />
                                    </div>
                                  </div>
                                  
                                  {/* Product Info and Actions - stacked vertically like Voila */}
                                  <div className="flex-1 flex flex-col justify-between min-h-[96px] sm:min-h-[112px]">
                                    <div className="mb-2">
                                      <div className="flex items-start justify-between">
                                        <h3 className="font-medium text-gray-900 text-sm sm:text-base mb-0.5">{searchCardLabels[index].title}<UnitPill unit={searchCardLabels[index].tags[0]} /></h3>
                                        {product.price && (
                                          <span className="text-sm font-medium text-gray-700 ml-2">${product.price}</span>
                                        )}
                                      </div>
                                      {product.subheading && (
                                        <p className="text-xs sm:text-sm text-gray-600 font-light">{product.subheading}</p>
                                      )}
                                    </div>
                                    
                                    {/* Add/Quantity Controls - full width buttons */}
                                    <div className="flex items-center">
                                      {currentQuantity === 0 ? (
                                        prevQuantities[product.id] !== undefined ? (
                                          <div className="flex items-center gap-2 w-full">
                                            <Button
                                              type="button"
                                              onClick={() => { field.onChange(1); }}
                                              className="bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white rounded-lg py-3 px-4 flex-1 h-11 font-medium"
                                              data-testid={`button-add-${product.id}`}
                                            >
                                              Add
                                            </Button>
                                            {/* Prev Qty mirrors Review Order: bg-blue-100/brand-blue is recall, brand-green is commit. */}
                                            <Button
                                              type="button"
                                              onClick={() => { field.onChange(prevQuantities[product.id]); }}
                                              className="bg-blue-100 text-cn-brand-blue hover:bg-cn-brand-blue hover:text-white rounded-lg py-3 px-4 flex-1 h-11 font-medium"
                                              data-testid={`button-prev-qty-${product.id}`}
                                            >
                                              {String(prevQuantities[product.id]).length >= 3 ? 'Prev Qty' : `Prev Qty: ${prevQuantities[product.id]}`}
                                            </Button>
                                          </div>
                                        ) : (
                                          <Button
                                            type="button"
                                            onClick={() => { field.onChange(1); }}
                                            className="bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white rounded-lg py-3 px-4 flex-1 h-11 font-medium"
                                            data-testid={`button-add-${product.id}`}
                                          >
                                            Add
                                          </Button>
                                        )
                                      ) : (
                                        <div className="flex items-center gap-2 w-full">
                                          <Button
                                            type="button"
                                            onClick={() => { field.onChange(Math.max(0, currentQuantity - 1)); }}
                                            className="bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white rounded-lg py-3 px-4 flex-1 h-11 font-medium"
                                            data-testid={`button-minus-${product.id}`}
                                          >
                                            <Minus className="h-4 w-4" />
                                          </Button>
                                          {editingQuantity === product.id ? (
                                            <Input
                                              type="number"
                                              value={editQuantityValue}
                                              onChange={(e) => setEditQuantityValue(e.target.value)}
                                              onBlur={() => handleQuantityInputSave(field)}
                                              onKeyDown={(e) => handleQuantityInputKeyDown(e, field)}
                                              className="font-medium text-lg text-center h-11 px-3 flex-1 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]"
                                              autoFocus
                                              data-testid={`input-quantity-${product.id}`}
                                            />
                                          ) : (
                                            <div className="flex-1 flex items-center justify-center h-11 bg-gray-50 rounded-lg mx-2 cursor-pointer hover:bg-gray-100 transition-colors"
                                              onClick={() => handleQuantityClick(currentQuantity, product.id)}
                                              data-testid={`text-quantity-${product.id}`}
                                            >
                                              <span className="font-medium text-lg">{currentQuantity}</span>
                                            </div>
                                          )}
                                          <Button
                                            type="button"
                                            onClick={() => { field.onChange(currentQuantity + 1); }}
                                            className="bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white rounded-lg py-3 px-4 flex-1 h-11 font-medium"
                                            data-testid={`button-plus-${product.id}`}
                                          >
                                            <Plus className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          />
                        );
                      })}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
              
            </AnimatePresence>
          </div>

          {/* Bottom Cart Bar - Animated */}
          <AnimatePresence>
            {(() => {
              const hasCurrentItems = totalItems > 0;
              const hasCurrentEditableOrders = currentPeriodOrders.length > 0;
              const hasRecentOrders = recentOrders.length > 0;
              
              // Show top part (Total Items + arrow) when there are items OR existing orders OR recent orders
              const showTopPart = hasCurrentItems || hasCurrentEditableOrders || hasRecentOrders;
              
              // Show button only when it would be enabled (i.e., when there are actionable changes)
              const showButton = (hasCurrentItems && !hasCurrentEditableOrders) || hasCurrentEditableOrders;
              
              // Show minimal bar when there are previous orders but no current activity and no button to show
              const showMinimalBar = !showButton && (hasCurrentEditableOrders || hasRecentOrders);

              // Zeroing every item on a SUBMITTED order turns the primary button into a red "Cancel
              // Order" — the customer cancels in the same familiar "submit" gesture, with Undo as the
              // safety net. Only for a bound (server) order; an empty new cart still says "Add an item".
              const cancelViaZero = cartDrawerMode === 'full-review' && !!boundOrder && totalItems === 0;

              if (!showTopPart && !showMinimalBar) return null;
              
              return (
                <motion.div
                  key="bottom-bar"
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "tween", duration: 0.3, ease: "easeOut" }}
                  className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg z-50"
                  style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
                >
                  <div className="px-4 py-2 mobile-container">
                    <div className="max-w-2xl mx-auto">
                      {showTopPart ? (
                        <>
                          {/* Total Items Bar with Up Arrow - Hide when quick-view is open since drawer has its own close */}
                          {cartDrawerMode === 'closed' && (() => {
                            let displayText = `Total Items: ${totalItems}`;
                            if (boundOrder && totalItems === 0) {
                              displayText = `Order #${boundOrder.id}`;
                            } else if (hasRecentOrders && totalItems === 0 && !hasCurrentEditableOrders) {
                              const mostRecentOrder = recentOrders.reduce((latest, current) => 
                                current.id > latest.id ? current : latest
                              );
                              displayText = `Order #${mostRecentOrder.id}`;
                            }
                            
                            return (
                              <div className="flex items-center justify-between gap-2 py-2">
                                {showOrderBar ? (
                                  <button
                                    type="button"
                                    onClick={openSwitcherSheet}
                                    className="flex items-center gap-2 min-w-0 -ml-1 py-1 pr-1 text-left rounded-md active:bg-gray-50"
                                    aria-haspopup="dialog"
                                    aria-expanded={switcherSheetOpen}
                                    data-testid="button-order-switcher-bottom"
                                  >
                                    {orderBarDate ? (
                                      orderBarMethod === 'pickup'
                                        ? <MapPin className="h-[18px] w-[18px] text-[#467B5A] flex-shrink-0" />
                                        : <Truck className="h-[18px] w-[18px] text-[#467B5A] flex-shrink-0" />
                                    ) : (
                                      <Plus className="h-[18px] w-[18px] text-[#467B5A] flex-shrink-0" />
                                    )}
                                    <span className="min-w-0">
                                      <span className="block text-[11px] leading-none text-gray-500 tracking-wide mb-0.5">
                                        {barIsExistingOrder ? 'Adding to' : 'New order'}
                                      </span>
                                      <span className="block text-sm font-medium text-gray-900 truncate">
                                        {orderBarDate ? <>{formatChipDate(orderBarDate)} <span className="text-gray-500 font-normal">· {orderBarMethod}</span></> : 'Choose a day'}
                                      </span>
                                    </span>
                                    <ChevronsUpDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                  </button>
                                ) : <span />}
                                <div
                                  className="flex items-center gap-3 py-1 cursor-pointer flex-shrink-0"
                                  onClick={toggleCartDetails}
                                  data-testid="button-cart-summary"
                                >
                                  <ShoppingCart className="h-5 w-5 text-gray-600" />
                                  <span className="text-gray-900 font-medium">
                                    {displayText}
                                  </span>
                                  <ChevronUp className="h-5 w-5 text-gray-600" />
                                </div>
                              </div>
                            );
                          })()}

                          {/* At checkout (full-review) the summary row is hidden, so surface the same
                              switcher here above Submit so the customer can hop carts without backing
                              out. "Switch" label + border keep it distinct from the date picker. */}
                          {cartDrawerMode === 'full-review' && showOrderBar && (
                            <button
                              type="button"
                              onClick={openSwitcherSheet}
                              className="w-full flex items-center justify-between gap-2 mb-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-left active:bg-gray-100"
                              aria-haspopup="dialog"
                              aria-expanded={switcherSheetOpen}
                              data-testid="button-order-switcher-checkout"
                            >
                              <span className="flex items-center gap-2 min-w-0">
                                {orderBarDate ? (
                                  orderBarMethod === 'pickup'
                                    ? <MapPin className="h-[18px] w-[18px] text-[#467B5A] flex-shrink-0" />
                                    : <Truck className="h-[18px] w-[18px] text-[#467B5A] flex-shrink-0" />
                                ) : (
                                  <Plus className="h-[18px] w-[18px] text-[#467B5A] flex-shrink-0" />
                                )}
                                <span className="min-w-0">
                                  <span className="block text-[11px] leading-none text-gray-500 tracking-wide mb-0.5">
                                    {barIsExistingOrder ? 'Adding to' : 'New order'}
                                  </span>
                                  <span className="block text-sm font-medium text-gray-900 truncate">
                                    {orderBarDate ? <>{formatChipDate(orderBarDate)} <span className="text-gray-500 font-normal">· {orderBarMethod}</span></> : 'Choose a day'}
                                  </span>
                                </span>
                              </span>
                              <span className="flex items-center gap-1 text-xs font-medium text-gray-500 flex-shrink-0">
                                Switch
                                <ChevronsUpDown className="h-4 w-4" />
                              </span>
                            </button>
                          )}

                          {/* Submit Button - Only show when enabled/actionable */}
                          <AnimatePresence>
                            {showButton && (
                              <motion.div 
                                key="submit-button"
                                initial={{ y: "100%", opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                exit={{ y: "100%", opacity: 0 }}
                                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                className="flex justify-stretch"
                              >
                                <Button
                                  type={(cartDrawerMode === 'full-review' && !cancelViaZero) ? "submit" : "button"}
                                  form={(cartDrawerMode === 'full-review' && !cancelViaZero) ? "order-form" : undefined}
                                  onClick={
                                    cancelViaZero
                                      ? () => { if (boundOrder) cancelOrderMutation.mutate(boundOrder); }
                                      : cartDrawerMode === 'full-review' ? undefined : handleReviewOrder
                                  }
                                  className={`w-full transition-all duration-200 text-base h-12 rounded-lg mb-2 font-semibold ${
                                    submitOrderMutation.isPending || cancelOrderMutation.isPending
                                      ? "bg-gray-400 cursor-not-allowed text-white"
                                      : cancelViaZero
                                        ? "bg-red-600 text-white hover:bg-red-700 hover:shadow-lg"
                                        : cartDrawerMode === 'full-review' && ((boundOrder && !hasChanges) || totalItems === 0)
                                          ? "bg-gray-200 text-gray-500 cursor-default"
                                          : cartDrawerMode === 'full-review'
                                            ? "bg-cn-brand-green text-white hover:shadow-lg hover:scale-[1.02]"
                                            : "bg-blue-100 text-cn-brand-blue hover:bg-cn-brand-blue hover:text-white hover:shadow-lg hover:scale-[1.02]"
                                  }`}
                                  disabled={submitOrderMutation.isPending || cancelOrderMutation.isPending || (!cancelViaZero && cartDrawerMode === 'full-review' && ((!!boundOrder && !hasChanges) || totalItems === 0))}
                                  data-testid="button-submit-order"
                                >
                                  {submitOrderMutation.isPending
                                    ? "Submitting..."
                                    : cancelOrderMutation.isPending
                                      ? "Cancelling..."
                                      : cancelViaZero
                                        ? "Cancel Order"
                                        : cartDrawerMode === 'full-review'
                                          ? (totalItems === 0
                                              ? "Add an item to continue"
                                              : boundOrder
                                                ? (hasChanges ? "Revise Order" : "Review Order")
                                                : "Submit Order")
                                          : (boundOrder
                                              ? (hasChanges ? "Review Changes" : "Review Order")
                                              : "Review Order")
                                  }
                                </Button>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </>
                      ) : (
                        /* Minimal Bar - Just Print and Up Arrow for Previous Orders */
                        (<div className="flex items-center justify-between py-2">
                          <div className="flex items-center gap-2">
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={handlePrintOrder}
                              data-testid="button-print-order-minimal"
                              className="text-gray-600 hover:text-gray-900"
                            >
                              <Printer className="h-5 w-5" />
                            </Button>
                          </div>
                          <div 
                            className="flex items-center gap-3 cursor-pointer"
                            onClick={toggleCartDetails}
                            data-testid="button-cart-summary-minimal"
                          >
                            <span className="text-gray-700 font-medium text-sm">
                              {hasCurrentEditableOrders
                                ? `Order #${boundOrder?.id || 'New'}`
                                : hasRecentOrders
                                  ? (() => {
                                      const mostRecentOrder = recentOrders.reduce((latest, current) => 
                                        current.id > latest.id ? current : latest
                                      );
                                      return `Order #${mostRecentOrder.id}`;
                                    })()
                                  : ''
                              }
                            </span>
                            <ChevronUp className="h-4 w-4 text-gray-600" />
                          </div>
                        </div>)
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })()}
          </AnimatePresence>

          {/* Order-switcher bottom sheet — thumb-reachable mirror of the top dropdown */}
          <AnimatePresence>
            {switcherSheetOpen && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="fixed inset-0 bg-black/30 z-[55]"
                  onClick={() => setSwitcherSheetOpen(false)}
                />
                <motion.div
                  key="switcher-sheet"
                  initial={{ y: "100%" }}
                  animate={{ y: 0 }}
                  exit={{ y: "100%" }}
                  transition={{ type: "tween", duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
                  className="fixed bottom-0 left-0 right-0 z-[60] bg-white rounded-t-2xl shadow-2xl max-h-[70dvh] overflow-y-auto"
                  style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
                  role="dialog"
                  aria-label="Switch order"
                >
                  <div className="flex justify-center pt-2.5 pb-1">
                    <div className="h-1 w-9 rounded-full bg-gray-300" aria-hidden />
                  </div>
                  {renderSwitcherList(() => setSwitcherSheetOpen(false))}
                </motion.div>
              </>
            )}
          </AnimatePresence>

          {/* Semi-transparent overlay */}
          <AnimatePresence>
            {cartDrawerMode !== 'closed' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="fixed inset-0 bg-cn-brand-blue/60 z-30"
                onClick={closeCart}
              />
            )}
          </AnimatePresence>

          {/* Cart Details Slide-up Panel */}
          <AnimatePresence>
            {cartDrawerMode !== 'closed' && (
              <motion.div
                key="cart-drawer"
                initial={{ y: "100%" }}
                animate={{ y: 0, height: cartDrawerMode === 'full-review' ? '85dvh' : '60dvh' }}
                exit={{ y: "100%" }}
                transition={{ type: "spring", stiffness: 300, damping: 30 }}
                className="fixed bg-white rounded-t-2xl responsive-popup shadow-2xl"
                style={{ 
                  bottom: 0,
                  zIndex: cartDrawerMode === 'quick-view' ? 30 : 40,
                  // Stop below the sticky header rather than under it (see stickyHeaderRef).
                  // Falls back to the old inset-only cap until the first measurement lands.
                  maxHeight: stickyHeaderHeight
                    ? `calc(100dvh - ${stickyHeaderHeight}px - 8px)`
                    : 'calc(100dvh - env(safe-area-inset-top, 0px) - 20px)',
                }}
              >
                <div className="flex flex-col h-full">
                  {/* Cart Header */}
                  <div className="bg-white border-b px-4 py-4 mobile-padding flex items-center justify-between rounded-t-xl flex-shrink-0">
                    <div className="max-w-2xl mx-auto w-full flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={closeCart}
                          className="text-gray-600 hover:text-gray-900 -ml-2"
                        >
                          <X className="h-5 w-5" />
                        </Button>
                        <h2 className="text-lg font-medium">
                          {cartTitle}
                        </h2>
                      </div>
                      <div className="flex items-center gap-2">
                        {cartDrawerMode === 'full-review' && !!boundOrder && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={handlePrintOrder}
                          data-testid="button-print-order"
                          className="text-gray-600 hover:text-gray-900"
                        >
                          <Printer className="h-5 w-5" />
                        </Button>
                        )}
                        
                        {/* Notification Preferences — not on the demo: a demo account
                            never gets a text or an email, and the server refuses the save. */}
                        {cartDrawerMode === 'full-review' && account && !organization?.isDemo && (
                          <Popover open={showNotificationPrefs} onOpenChange={setShowNotificationPrefs}>
                            <PopoverTrigger asChild>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                data-testid="button-bell"
                                className="text-gray-600 hover:text-gray-900"
                              >
                                <Bell className="h-5 w-5" />
                              </Button>
                            </PopoverTrigger>
                            <PopoverContent className="w-64" align="end">
                              <div className="space-y-3">
                                <h4 className="font-medium text-base text-cn-brand-blue">Notifications</h4>
                                
                                {/* SMS Section */}
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <MessageSquare className="h-4 w-4 text-cn-brand-blue" />
                                    <Label className="text-sm font-semibold text-cn-brand-blue">SMS</Label>
                                  </div>
                                  
                                  {/* SMS Reminders — hidden when reminders are admin-managed */}
                                  {!(account?.firstReminderDay !== null && account?.firstReminderDay !== undefined) && (
                                  <div className="flex items-center justify-between ml-6">
                                    <Label className="text-sm font-medium text-cn-brand-blue">Reminders</Label>
                                    <div className="flex items-center space-x-2">
                                      <Switch
                                        checked={notificationSettings.reminderEnabled}
                                        onCheckedChange={(checked) => handleNotificationToggle('reminderEnabled', checked)}
                                        disabled={updateNotificationMutation.isPending}
                                        data-testid="switch-sms-reminders"
                                        className="hidden sm:flex"
                                      />
                                      <button
                                        onClick={() => handleNotificationToggle('reminderEnabled', !notificationSettings.reminderEnabled)}
                                        disabled={updateNotificationMutation.isPending}
                                        className={`sm:hidden px-3 py-1 rounded-md text-sm font-medium transition-colors disabled:opacity-50 ${
                                          notificationSettings.reminderEnabled
                                            ? "bg-cn-brand-green text-white"
                                            : "bg-gray-200 text-gray-600"
                                        }`}
                                        data-testid="toggle-sms-reminders"
                                      >
                                        {notificationSettings.reminderEnabled ? 'ON' : 'OFF'}
                                      </button>
                                    </div>
                                  </div>
                                  )}
                                  
                                  {/* SMS Order Summary */}
                                  <div className="flex items-center justify-between ml-6">
                                    <Label className="text-sm font-medium text-cn-brand-blue">Order Summary</Label>
                                    <div className="flex items-center space-x-2">
                                      <Switch
                                        checked={notificationSettings.smsOrderReceipts}
                                        onCheckedChange={(checked) => handleNotificationToggle('smsOrderReceipts', checked)}
                                        disabled={updateNotificationMutation.isPending}
                                        data-testid="switch-sms-receipts"
                                        className="hidden sm:flex"
                                      />
                                      <button
                                        onClick={() => handleNotificationToggle('smsOrderReceipts', !notificationSettings.smsOrderReceipts)}
                                        disabled={updateNotificationMutation.isPending}
                                        className={`sm:hidden px-3 py-1 rounded-md text-sm font-medium transition-colors disabled:opacity-50 ${
                                          notificationSettings.smsOrderReceipts
                                            ? "bg-cn-brand-green text-white"
                                            : "bg-gray-200 text-gray-600"
                                        }`}
                                        data-testid="toggle-sms-receipts"
                                      >
                                        {notificationSettings.smsOrderReceipts ? 'ON' : 'OFF'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Email Section */}
                                <div className="space-y-2">
                                  <div className="flex items-center gap-2">
                                    <Mail className="h-4 w-4 text-cn-brand-blue" />
                                    <Label className="text-sm font-semibold text-cn-brand-blue">Email</Label>
                                  </div>
                                  
                                  {/* Email Reminders — hidden when reminders are admin-managed */}
                                  {!(account?.firstReminderDay !== null && account?.firstReminderDay !== undefined) && (
                                  <div className="flex items-center justify-between ml-6">
                                    <Label className="text-sm font-medium text-cn-brand-blue">Reminders</Label>
                                    <div className="flex items-center space-x-2">
                                      <Switch
                                        checked={notificationSettings.emailNotificationEnabled}
                                        onCheckedChange={(checked) => handleNotificationToggle('emailNotificationEnabled', checked)}
                                        disabled={updateNotificationMutation.isPending}
                                        data-testid="switch-email-reminders"
                                        className="hidden sm:flex"
                                      />
                                      <button
                                        onClick={() => handleNotificationToggle('emailNotificationEnabled', !notificationSettings.emailNotificationEnabled)}
                                        disabled={updateNotificationMutation.isPending}
                                        className={`sm:hidden px-3 py-1 rounded-md text-sm font-medium transition-colors disabled:opacity-50 ${
                                          notificationSettings.emailNotificationEnabled
                                            ? "bg-cn-brand-green text-white"
                                            : "bg-gray-200 text-gray-600"
                                        }`}
                                        data-testid="toggle-email-reminders"
                                      >
                                        {notificationSettings.emailNotificationEnabled ? 'ON' : 'OFF'}
                                      </button>
                                    </div>
                                  </div>
                                  )}
                                  
                                  {/* Email Order Summary */}
                                  <div className="flex items-center justify-between ml-6">
                                    <Label className="text-sm font-medium text-cn-brand-blue">Order Summary</Label>
                                    <div className="flex items-center space-x-2">
                                      <Switch
                                        checked={notificationSettings.emailOrderReceipts}
                                        onCheckedChange={(checked) => handleNotificationToggle('emailOrderReceipts', checked)}
                                        disabled={updateNotificationMutation.isPending}
                                        data-testid="switch-email-receipts"
                                        className="hidden sm:flex"
                                      />
                                      <button
                                        onClick={() => handleNotificationToggle('emailOrderReceipts', !notificationSettings.emailOrderReceipts)}
                                        disabled={updateNotificationMutation.isPending}
                                        className={`sm:hidden px-3 py-1 rounded-md text-sm font-medium transition-colors disabled:opacity-50 ${
                                          notificationSettings.emailOrderReceipts
                                            ? "bg-cn-brand-green text-white"
                                            : "bg-gray-200 text-gray-600"
                                        }`}
                                        data-testid="toggle-email-receipts"
                                      >
                                        {notificationSettings.emailOrderReceipts ? 'ON' : 'OFF'}
                                      </button>
                                    </div>
                                  </div>
                                </div>
                                
                                {updateNotificationMutation.isPending && (
                                  <div className="text-center text-sm text-gray-500 pt-2">
                                    Updating...
                                  </div>
                                )}
                              </div>
                            </PopoverContent>
                          </Popover>
                        )}
                        
                      </div>
                    </div>
                  </div>

                  {/* Scrollable Cart Content */}
                  <div className="flex-1 overflow-y-auto px-4 py-4 mobile-container" style={{ height: 'calc(100% - 80px)', paddingBottom: '80px' }}>
                    <div className="max-w-2xl mx-auto">
                    {/* The in-cart order switcher moved to the omni-present header bar, which floats
                        above this drawer (header z-50 vs drawer z-40) — which is why the drawer is
                        height-capped to stop below it. The cart header below names the order being
                        reviewed, so a duplicate in-cart chip strip would only add the confusion of
                        showing another order's date at the top of this cart. */}

                    {/* New, empty order: confirm + send the customer to the menu, rather than a blank cart. */}
                    {cartDrawerMode === 'full-review' && !boundOrder && currentPeriodOrders.length > 0 && totalItems === 0 && (
                      <div className="text-center py-6 mb-2">
                        <ShoppingCart className="h-7 w-7 mx-auto mb-2 text-gray-300" />
                        {activeFulfillmentDate ? (
                          <>
                            <div className="text-sm text-gray-600 mb-3">
                              New {fulfillmentType === "pickup" ? "pickup" : "delivery"} order for {formatDayName(activeFulfillmentDate)}
                            </div>
                            <button
                              type="button"
                              onClick={() => setCartDrawerMode('closed')}
                              className="inline-flex items-center gap-1.5 bg-cn-brand-green text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-[#3A6247]"
                              data-testid="button-start-new-order"
                            >
                              Start {formatChipDate(activeFulfillmentDate)} order
                              <ChevronRight className="h-4 w-4" />
                            </button>
                            <div className="text-xs text-gray-400 mt-2">Add items from the menu, then submit.</div>
                          </>
                        ) : (
                          <div className="text-sm text-gray-600">Pick a day below to start a new order.</div>
                        )}
                      </div>
                    )}
                    <div className="space-y-4 mb-4">
                      {Object.entries(orderItemsByCategory).map(([category, items]) => (
                        <div key={category}>
                          <div className="font-semibold text-gray-700 text-sm mb-2">{category}</div>
                          <div className="space-y-2 ml-4">
                            {items.map((item, index) => (
                              <div key={index} className="flex items-center gap-3 border border-gray-200 rounded-lg p-3 mobile-padding">
                                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                                  <img
                                    src={getProductImage(item.product, products.find(p => p.name === item.product), organization?.faviconUrl)}
                                    alt={item.product}
                                    className="w-full h-full object-cover rounded-lg"
                                    loading="lazy"
                                    decoding="async"
                                  />
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-gray-900">
                                    {item.product}
                                    {item.price && (
                                      <span className="text-sm font-normal text-gray-500 ml-2">
                                        ${item.price}{item.taxPercent && parseFloat(item.taxPercent) > 0 ? ' +tax' : ''}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const safeKey = productKey(item.productId);
                                      form.setValue(`quantities.${safeKey}`, Math.max(0, item.quantity - 1));
                                    }}
                                    className="h-8 w-8 p-0 rounded-md bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white border-[hsl(140,27%,88%)]"
                                    data-testid={`cart-minus-${category}-${index}`}
                                  >
                                    <Minus className="h-4 w-4" />
                                  </Button>
                                  <span className="font-medium min-w-[2rem] text-center">
                                    {item.quantity}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    onClick={() => {
                                      const safeKey = productKey(item.productId);
                                      form.setValue(`quantities.${safeKey}`, item.quantity + 1);
                                    }}
                                    className="h-8 w-8 p-0 rounded-md bg-[hsl(140,27%,88%)] text-cn-brand-green hover:bg-cn-brand-green hover:text-white border-[hsl(140,27%,88%)]"
                                    data-testid={`cart-plus-${category}-${index}`}
                                  >
                                    <Plus className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Estimated total — only when every item in the cart has a price.
                        Each priced item still shows its own posted price above. */}
                    {(() => {
                      const totals = orderTotals(orderItems);
                      if (!totals) return null;
                      return (
                        <div className="bg-gray-50 rounded-lg p-4 mb-4 border border-gray-200" data-testid="cart-totals">
                          <div className="flex justify-between items-center mb-2">
                            <span className="text-gray-600">Subtotal</span>
                            <span className="text-gray-900">${totals.subtotal.toFixed(2)}</span>
                          </div>
                          {totals.tax > 0 && (
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-gray-600">Tax</span>
                              <span className="text-gray-900">${totals.tax.toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between items-center pt-2 border-t border-gray-200">
                            <span className="font-semibold text-gray-900">Total</span>
                            <span className="font-bold text-lg text-cn-brand-green">${totals.total.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Pricing Disclaimer */}
                    {cartDrawerMode === 'full-review' && (
                    <p className="text-xs text-gray-400 text-center mb-4">
                      Prices subject to change without notice.
                    </p>
                    )}

                    {/* Fulfillment Method & Date Selector — shown in full review for both new orders
                        and edits. Picking a different day/method while editing an order opens the
                        move-vs-separate prompt (recreate model). Days another order already occupies
                        are disabled. */}
                    {cartDrawerMode === 'full-review' && (deliveryEnabled || pickupEnabled) && (
                      <div className="mb-6">
                        {showFulfillmentSelector && (
                          <div className="mb-4">
                            <label className="text-base font-semibold text-gray-900 mb-3 block">
                              How would you like to receive your order?
                            </label>
                            <div className="flex items-stretch gap-3">
                              {(() => {
                                const deliveryReason = methodUnavailableReason("delivery");
                                const pickupReason = methodUnavailableReason("pickup");
                                return (<>
                              <button
                                type="button"
                                onClick={() => requestMethod("delivery")}
                                disabled={!!deliveryReason}
                                aria-disabled={!!deliveryReason}
                                className={`flex-1 flex flex-col items-center justify-center gap-1 rounded-xl px-4 py-3 border-2 transition-all font-medium ${
                                  fulfillmentType === "delivery"
                                    ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                                    : deliveryReason
                                      ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                }`}
                              >
                                <span className="flex items-center gap-2">
                                  <Truck className="h-5 w-5" />
                                  Delivery
                                </span>
                                {deliveryReason && <span className="text-xs font-normal text-gray-500 text-center leading-tight">{deliveryReason}</span>}
                              </button>
                              <button
                                type="button"
                                onClick={() => requestMethod("pickup")}
                                disabled={!!pickupReason}
                                aria-disabled={!!pickupReason}
                                className={`flex-1 flex flex-col items-center justify-center gap-1 rounded-xl px-4 py-3 border-2 transition-all font-medium ${
                                  fulfillmentType === "pickup"
                                    ? 'border-green-500 bg-green-50 text-green-700 shadow-sm'
                                    : pickupReason
                                      ? 'border-gray-200 bg-gray-50 text-gray-400 cursor-not-allowed'
                                      : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                                }`}
                              >
                                <span className="flex items-center gap-2">
                                  <MapPin className="h-5 w-5" />
                                  Pickup
                                </span>
                                {pickupReason && <span className="text-xs font-normal text-gray-500 text-center leading-tight">{pickupReason}</span>}
                              </button>
                                </>);
                              })()}
                            </div>
                          </div>
                        )}

                        <label className="text-base font-semibold text-gray-900 mb-3 block" data-testid="label-fulfillment-day">
                          <Calendar className="inline h-4 w-4 mr-1.5 -mt-0.5" />
                          {fulfillmentType === "pickup" ? "Pickup Day" : "Delivery Date"} <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-1.5 w-full">
                          {(() => {
                            if (!windowConfig) return null;
                            const dates = getAvailableFulfillmentDates(fulfillmentType, windowConfig);
                            if (dates.length === 0) {
                              return (
                                <p className="text-sm text-gray-500 py-2">No {fulfillmentType === "pickup" ? "pickup" : "delivery"} dates available right now. Please contact us for assistance.</p>
                              );
                            }
                            const visibleDates = dates.slice(0, 4);
                            const hasMore = dates.length > 4;
                            const availableDateSet = new Set(dates.map(d => d.value));
                            const selectedIsHidden = fulfillmentDate && !visibleDates.some(d => d.value === fulfillmentDate) && availableDateSet.has(fulfillmentDate);
                            const selectedHiddenDate = selectedIsHidden ? dates.find(d => d.value === fulfillmentDate) : null;

                            return (
                              <>
                                {visibleDates.map((day) => {
                                  const isTaken = isDateTakenByOther(day.value, fulfillmentType);
                                  const isSelected = fulfillmentDate === day.value;
                                  return (
                                  <button
                                    key={day.value}
                                    type="button"
                                    disabled={isTaken}
                                    onClick={() => { if (!isTaken) requestDateChange(day.value, fulfillmentType); }}
                                    className={`flex-1 min-w-0 flex flex-col items-center justify-center rounded-xl py-2 px-1 border-2 transition-all ${
                                      isTaken
                                        ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                                        : isSelected
                                          ? fulfillmentType === "pickup"
                                            ? 'border-green-500 bg-green-50 text-green-700 shadow-sm'
                                            : 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                                    }`}
                                  >
                                    <span className={`text-xs sm:text-sm font-semibold truncate w-full text-center ${
                                      isTaken
                                        ? 'text-gray-500'
                                        : isSelected
                                          ? fulfillmentType === "pickup" ? 'text-green-700' : 'text-blue-700'
                                          : 'text-gray-900'
                                    }`}>
                                      {day.label}
                                    </span>
                                    <span className={`text-[10px] sm:text-xs ${
                                      isTaken
                                        ? 'text-gray-500'
                                        : isSelected
                                          ? fulfillmentType === "pickup" ? 'text-green-600/70' : 'text-blue-600/70'
                                          : 'text-gray-500'
                                    }`}>
                                      {isTaken ? 'ordered' : day.sublabel}
                                    </span>
                                  </button>
                                  );
                                })}
                                {hasMore && (
                                  <Popover>
                                    <PopoverTrigger asChild>
                                      <button
                                        type="button"
                                        className={`flex-1 min-w-0 flex flex-col items-center justify-center rounded-xl py-2 px-1 border-2 transition-all ${
                                          selectedIsHidden
                                            ? fulfillmentType === "pickup"
                                              ? 'border-green-500 bg-green-50 text-green-700 shadow-sm'
                                              : 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm'
                                            : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                                        }`}
                                      >
                                        <span className={`text-xs sm:text-sm font-semibold truncate w-full text-center ${
                                          selectedIsHidden
                                            ? fulfillmentType === "pickup" ? 'text-green-700' : 'text-blue-700'
                                            : 'text-gray-900'
                                        }`}>
                                          {selectedHiddenDate ? selectedHiddenDate.label : 'More'}
                                        </span>
                                        <span className={`text-[10px] sm:text-xs ${
                                          selectedIsHidden
                                            ? fulfillmentType === "pickup" ? 'text-green-600/70' : 'text-blue-600/70'
                                            : 'text-gray-500'
                                        }`}>
                                          {selectedHiddenDate ? selectedHiddenDate.sublabel : '...'}
                                        </span>
                                      </button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="center">
                                      <CalendarWidget
                                        mode="single"
                                        selected={fulfillmentDate ? new Date(fulfillmentDate + 'T12:00:00') : undefined}
                                        onSelect={(date) => {
                                          if (date) {
                                            const y = date.getFullYear();
                                            const m = String(date.getMonth() + 1).padStart(2, '0');
                                            const d = String(date.getDate()).padStart(2, '0');
                                            const dateStr = `${y}-${m}-${d}`;
                                            if (availableDateSet.has(dateStr) && !isDateTakenByOther(dateStr, fulfillmentType)) {
                                              requestDateChange(dateStr, fulfillmentType);
                                            }
                                          }
                                        }}
                                        disabled={(date) => {
                                          const y = date.getFullYear();
                                          const m = String(date.getMonth() + 1).padStart(2, '0');
                                          const d = String(date.getDate()).padStart(2, '0');
                                          const ds = `${y}-${m}-${d}`;
                                          return !availableDateSet.has(ds) || isDateTakenByOther(ds, fulfillmentType);
                                        }}
                                        modifiers={{
                                          available: dates.map(d => new Date(d.value + 'T12:00:00')),
                                        }}
                                        modifiersStyles={{
                                          available: fulfillmentType === "pickup"
                                            ? { backgroundColor: '#f0fdf4', color: '#15803d', fontWeight: '600' }
                                            : { backgroundColor: '#eff6ff', color: '#1d4ed8', fontWeight: '600' },
                                        }}
                                        initialFocus
                                      />
                                    </PopoverContent>
                                  </Popover>
                                )}
                              </>
                            );
                          })()}
                        </div>

                        {showDeadlineMessages && fulfillmentDate && deadlineTimeLeft && (
                          <div className="bg-gray-50 rounded-lg p-4 mt-4">
                            <div className="flex items-center gap-3 mb-2">
                              <Clock className="h-5 w-5 text-cn-orange" />
                              <span className="font-semibold text-gray-900 text-base">Order Deadline</span>
                            </div>
                            <p className="text-sm text-gray-600 leading-relaxed">
                              You have <span className="text-[#C2410C] font-bold">{deadlineTimeLeft}</span> to {boundOrder ? 'make changes to' : 'submit'} your order for {fulfillmentType === "pickup" ? "pickup" : "delivery"} {(() => { const today = new Date(); const [y, m, d] = (fulfillmentDate || '').split('-').map(Number); return (y === today.getFullYear() && m === today.getMonth() + 1 && d === today.getDate()) ? <><span className="text-[#C2410C] font-bold">today</span></> : <>this <span className="text-[#C2410C] font-bold">{deadlineDayName}</span></>; })()}.
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Cancel a submitted order (soft cancel, recoverable) OR discard an unsent draft
                        order (clears it locally). Shown when editing a submitted order, or on a draft
                        that has items while other orders exist (so a stray draft can be cleared). */}
                    {cartDrawerMode === 'full-review' && (boundOrder || (totalItems > 0 && currentPeriodOrders.length > 0)) && (
                      <div className="mb-6">
                        <button
                          type="button"
                          onClick={() => setCancelConfirmOpen(true)}
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 hover:text-red-700 hover:underline"
                          data-testid="button-cancel-order"
                        >
                          <X className="h-4 w-4" />
                          {boundOrder ? 'Cancel this order' : 'Discard this order'}
                        </button>
                      </div>
                    )}

                    {/* Notes Section - only in full review */}
                    {cartDrawerMode === 'full-review' && (
                    <div className="mb-8">
                      <FormField
                        control={form.control}
                        name="notes"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-base font-semibold text-gray-900 mb-3 block">
                              Order Notes (Optional)
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                {...field}
                                onChange={(e) => { field.onChange(e); }}
                                placeholder="Add any special instructions..."
                                className="resize-none h-24 text-base p-4 leading-relaxed"
                                data-testid="textarea-cart-notes"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    )}
                    
                    </div>
                  </div>


                </div>
              </motion.div>
            )}
          </AnimatePresence>



        </form>
      </Form>

      {/* App-store requirement (Apple 5.1.1(v) / Google Play): account deletion must be reachable in-app.
          pb-28 keeps it clear of the fixed bottom order bar; the views above are all min-h-screen so
          this sits at the true page bottom, never floating mid-screen under short content. */}
      <footer className="text-center pt-6 pb-28 px-4">
        <Link
          href="/account-deletion"
          className="text-xs text-gray-400 underline hover:text-gray-600"
          data-testid="link-account-deletion"
        >
          Request account deletion
        </Link>
      </footer>

      {/* Move-vs-separate intent prompt (recreate model): picking a different day/method while
          editing an order asks whether to MOVE that order or start a SEPARATE one. */}
      <AlertDialog open={pendingMove != null} onOpenChange={(open) => { if (!open) setPendingMove(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Change or add order?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You can move this order to the chosen date, or start a new order for the chosen date.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <AlertDialogAction
              onClick={() => {
                if (pendingMove) {
                  setMoveCancelOrderId(boundOrder?.id ?? null);
                  if (boundOrder) {
                    const srcType = (boundOrder.fulfillmentType || 'delivery') as 'delivery' | 'pickup';
                    const srcDate = boundOrder.fulfillmentDate || boundOrder.pickupDate || '';
                    setMoveCancelOrderFromKey(cartDateKey(srcDate, srcType === 'pickup' ? srcDate : "", srcType));
                  } else {
                    setMoveCancelOrderFromKey(null);
                  }
                  changeDateKeepCart(pendingMove.date, pendingMove.type);
                }
                setPendingMove(null);
              }}
              className="bg-cn-brand-green hover:bg-[#3A6247] w-full"
            >
              Move this order to {pendingMove ? formatChipDate(pendingMove.date) : ''}
            </AlertDialogAction>
            <button
              type="button"
              onClick={() => {
                if (pendingMove) bindToDate(pendingMove.date, pendingMove.type);
                setPendingMove(null);
              }}
              className="w-full rounded-md border border-gray-300 px-4 py-2 text-sm font-medium hover:bg-gray-50"
            >
              Keep {boundOrder ? formatChipDate(boundOrder.fulfillmentDate || boundOrder.pickupDate || '') : ''}, start a separate order
            </button>
            <AlertDialogCancel className="mt-0 w-full">Never mind</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Cancel-order confirm (soft cancel). */}
      <AlertDialog open={cancelConfirmOpen} onOpenChange={setCancelConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{boundOrder ? 'Cancel this order?' : 'Discard this order?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {boundOrder
                ? `Your ${(boundOrder.fulfillmentType || 'delivery') === 'pickup' ? 'pickup' : 'delivery'} order for ${formatChipDate(boundOrder.fulfillmentDate || boundOrder.pickupDate || '')} will be cancelled. You can place a new order any time before the cutoff.`
                : "This order hasn't been submitted yet. It will be removed from your cart."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{boundOrder ? 'Keep order' : 'Keep editing'}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (boundOrder) cancelOrderMutation.mutate(boundOrder); else discardDraft(); }}
              className="bg-red-600 hover:bg-red-700"
              disabled={cancelOrderMutation.isPending}
            >
              {boundOrder ? 'Cancel order' : 'Discard order'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Kiosk Delivery Location Dialog */}
      <Dialog open={showDeliveryLocationDialog} onOpenChange={setShowDeliveryLocationDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <MapPin className="h-5 w-5 text-cn-brand-blue" />
              Where should we bring your order?
            </DialogTitle>
            <DialogDescription>
              Enter your bay door number or parking spot so we can deliver your order.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Input
              placeholder="e.g., Bay Door 3, Parking Spot A12"
              value={deliveryLocation}
              onChange={(e) => setDeliveryLocation(e.target.value)}
              className="text-lg h-12"
              autoFocus
              data-testid="input-delivery-location"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={handleSkipDeliveryLocation}
                className="flex-1 h-12"
                data-testid="button-skip-location"
              >
                Skip
              </Button>
              <Button
                onClick={handleDeliveryLocationSubmit}
                className="flex-1 h-12 bg-cn-brand-blue hover:bg-cn-brand-blue/90"
                disabled={!deliveryLocation.trim()}
                data-testid="button-save-location"
              >
                <Truck className="h-4 w-4 mr-2" />
                Save Location
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Kiosk Confirmation Dialog */}
      <Dialog open={showKioskConfirmation} onOpenChange={setShowKioskConfirmation}>
        <DialogContent className="sm:max-w-md text-center">
          <div className="py-6 space-y-4">
            <div className="w-20 h-20 bg-cn-brand-green/10 rounded-full flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-12 w-12 text-cn-brand-green" />
            </div>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Order Received!
            </DialogTitle>
            <DialogDescription className="text-lg text-gray-600">
              {confirmedDeliveryLocation 
                ? `We'll text you when it's on the way to ${confirmedDeliveryLocation}.`
                : "We'll text you when it's ready."}
            </DialogDescription>
            <p className="text-sm text-gray-400 mt-4">
              Returning to start screen in 5 seconds...
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Verification Dialog */}
      <Dialog open={showEmailVerificationDialog} onOpenChange={(open) => {
        setShowEmailVerificationDialog(open);
        if (!open) {
          setEmailToVerify("");
          setPendingEmailFeature(null);
          setEmailVerificationSent(false);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl flex items-center gap-2">
              <Mail className="h-5 w-5 text-cn-brand-blue" />
              {emailVerificationSent ? "Check Your Email" : "Add Your Email"}
            </DialogTitle>
            <DialogDescription>
              {emailVerificationSent 
                ? "We've sent you a verification link. Click it to confirm your email and enable this feature."
                : `To receive ${pendingEmailFeature === 'emailOrderReceipts' ? 'order receipts' : 'email notifications'}, please add and verify your email address.`}
            </DialogDescription>
          </DialogHeader>
          
          {!emailVerificationSent ? (
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="email-input">Email Address</Label>
                <Input
                  id="email-input"
                  type="email"
                  placeholder="your@email.com"
                  value={emailToVerify}
                  onChange={(e) => setEmailToVerify(e.target.value)}
                  className="text-lg h-12"
                  autoFocus
                  data-testid="input-email-verification"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSubmitEmailVerification();
                    }
                  }}
                />
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowEmailVerificationDialog(false)}
                  className="flex-1 h-12"
                  data-testid="button-cancel-email"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmitEmailVerification}
                  className="flex-1 h-12 bg-cn-brand-blue hover:bg-cn-brand-blue/90"
                  disabled={!emailToVerify.trim() || requestEmailVerificationMutation.isPending}
                  data-testid="button-send-verification"
                >
                  {requestEmailVerificationMutation.isPending ? (
                    "Sending..."
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Verification
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <div className="py-6 space-y-4 text-center">
              <div className="w-16 h-16 bg-cn-brand-blue/10 rounded-full flex items-center justify-center mx-auto">
                <Mail className="h-8 w-8 text-cn-brand-blue" />
              </div>
              <p className="text-gray-600">
                Check your inbox at <span className="font-medium text-gray-900">{emailToVerify}</span> and click the verification link.
              </p>
              <p className="text-sm text-gray-500">
                The link expires in 24 hours.
              </p>
              <Button
                variant="outline"
                onClick={() => setShowEmailVerificationDialog(false)}
                className="w-full h-12 mt-4"
              >
                Got it
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
